import React, { useState, useEffect, useCallback, useMemo, useRef, memo } from 'react';
import { useNavigate, useSearchParams, useLocation } from 'react-router';
import { useAuthContext } from '../contexts/AuthContext';
import { useTheme, createThemedStyles } from '../contexts/ThemeContext';
import { Navigation } from '../components/Navigation';
import { CustomToggle } from '../components/CustomToggle';
import { ItemCard } from '../components/ItemCard';
import { caseApiService } from '../services/caseApiService';
import { pdfApiService } from '../services/pdfApiService';
import { wordApiService } from '../services/wordApiService';
import { CaseSummary, CaseSentSummary, CaseReceivedSummary } from '../types/caseApi';
import { CaseData } from '../types/case';
import { createNewCase, NO_TAX_YEAR_AVAILABLE_MESSAGE } from '../utils/createNewCase';
import { getDefaultTaxYear, isCalcVersionDisabledError } from '../utils/calcVersionAvailability';
import { useSystemSettingsContext } from '../contexts/SystemSettingsContext';
// import { CaseStateManager, CaseEvents } from '../services/caseStateManager'; // Removed - using simple architecture
import Select from 'react-select';
import { useAlert } from '../contexts/AlertContext';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEdit, faStar, faEye, faInfoCircle, faCopy, faDownload, faSort, faSortDown, faSortUp, faTimes, faBan, faRotateLeft, faCheckCircle, faExclamationTriangle, faTimesCircle, faClock, faCheck, faSync, faFileImport, faFileExport, faPlus, faGem, faReceipt } from '@/icons';
import { SpinnerOverlay } from '../components/SpinnerOverlay';
import { TutorialOverlay } from '../components/TutorialOverlay';
import { TutorialService } from '../services/tutorialService';
import { CustomTooltip } from '../components/CustomTooltip';
import { CaseListDownloadModal } from '../components/CaseListDownloadModal';
import { AlimonyScenarioSelectModal } from '../components/AlimonyScenarioSelectModal';

/** The download-menu label, matched against what the modal reports back. */
const ALIMONY_ANALYSIS_DOWNLOAD_OPTION = 'Alimony Analysis';
import { debugError, debugLog } from '../utils/debug';
import { getPdfMapByName } from '../utils/pdfTypeMaps';
import { CaseNoteModal } from '../components/CaseNoteModal';
import { SharingUpsell } from '../components/SharingUpsell';
import { SentCasesTable } from '../components/SentCasesTable';
import { ReceivedCasesPanel } from '../components/ReceivedCasesPanel';
import { DownloadNotAvailableModal } from '../components/DownloadNotAvailableModal';
import { ProUpgradeModal } from '../components/ProUpgradeModal';
import { RescindRecipientsModal } from '../components/RescindRecipientsModal';
import { InfoModal } from '../components/InfoModal';
import { ProDataReasonsNotice } from '../components/ProDataReasonsNotice';
import { CopyCaseModal } from '../components/CopyCaseModal';
import { isCopyInProgressError, copyInProgressMessage } from '../utils/copyInProgress';
import { AcceptCaseModal } from '../components/AcceptCaseModal';
import { AcceptCaseSuccessModal } from '../components/AcceptCaseSuccessModal';
import { ExportCaseModal } from '../components/ExportCaseModal';
import { ExportCaseZipModal } from '../components/ExportCaseZipModal';
import { AnnouncementModal } from '../components/AnnouncementModal';
import { ConfirmationModal } from '../components/Modal';
import { notificationApiService } from '../services/notificationApiService';
import { useFeatureFlag } from '../hooks/useFeatureFlag';
import { useIsProSubscription } from '../hooks/useEffectiveSubscriptionType';
import { isProDowngradeLocked } from '../hooks/useProDowngrade';
import { useTeamBillingState } from '../hooks/useTeamBillingState';
import { useCaseEditGuard } from '../hooks/useCaseEditGuard';
import { CaseStatusDropdown } from '../components/CaseStatusDropdown';
import { isTeamBillingReadOnlyError } from '../utils/teamBillingReadOnly';
import { useVisibilityAwarePolling } from '../hooks/useVisibilityAwarePolling';
import { getTabId } from '../utils/clientIdentifiers';
import { subscribeToPresence } from '../utils/caseEditingPresenceChannel';
import { featureUsageApiService } from '../services/featureUsageApiService';
import { CardTable } from '../components/CardTable';
import { ScrollableTabStrip } from '../components/ScrollableTabStrip';


/**
 * Home page component - Team Cases interface
 */
export const HomePage: React.FC = () => {
    const theme = useTheme();
    const themedStyles = createThemedStyles(theme);
    const { addAlert } = useAlert();
    const navigate = useNavigate();
    const location = useLocation();
    const [searchParams] = useSearchParams();
    const { user: currentUser } = useAuthContext();
    const notificationsEnabled = useFeatureFlag('Notifications');
    const proFeatureTrackerEnabled = useFeatureFlag('ProFeatureTracker');
    const concurrentCaseEditGuardEnabled = useFeatureFlag('ConcurrentCaseEditGuard');
    const proDowngradeEnabled = useFeatureFlag('ProDowngrade');

    // Determine active tab based on URL path
    const getActiveTabFromPath = useCallback(() => {
        if (location.pathname === '/cases/received') {
            return 'received';
        }
        if (location.pathname === '/cases/sent') {
            return 'sent';
        }
        return 'myTeam'; // Default to 'myTeam' for /cases/list or /
    }, [location.pathname]);

    // Initialize activeTab from URL
    const [activeTab, setActiveTab] = useState<'myTeam' | 'received' | 'sent'>(getActiveTabFromPath());

    // Get attorneyId from URL parameters (only used for Received/Sent tabs, not My Team's Cases)
    const attorneyIdFromUrl = activeTab !== 'myTeam' ? searchParams.get('attorneyId') : null;

    // Update activeTab when URL changes
    useEffect(() => {
        setActiveTab(getActiveTabFromPath());
    }, [getActiveTabFromPath]);

    // Check if user has Pro subscription. Uses the shared hook so the admin/dev subscription
    // override (SubscriptionToggleFAB) applies here too -- this page used to read the team value
    // directly and was the one major surface that ignored the override.
    const isPro = useIsProSubscription();

    // BillingDunning: while the team is read-only the server refuses new cases, copies and
    // imports, so the buttons that start them are disabled.
    const { isReadOnly: isBillingReadOnly } = useTeamBillingState();
    // A new case takes the default tax year; with none enabled, Add New is disabled.
    const { calcVersions, isLoading: systemSettingsLoading } = useSystemSettingsContext();
    const newCaseTaxYearMissing = getDefaultTaxYear('IA', calcVersions) === null;
    const newCaseBlocked = systemSettingsLoading || newCaseTaxYearMissing;
    const noTaxYearTooltip = !systemSettingsLoading && newCaseTaxYearMissing ? NO_TAX_YEAR_AVAILABLE_MESSAGE : undefined;

    // ProDowngrade: a case that holds Pro-only data is read-only for a team that is no longer Pro
    // -- it can only be copied or downloaded. Read during render only, so plain useFeatureFlag is
    // safe here (no mount-once effect depends on it).
    const isCaseProLocked = useCallback(
        (caseItem: CaseSummary) => isProDowngradeLocked(proDowngradeEnabled, isPro ? 'pro' : undefined, caseItem.cpd),
        [proDowngradeEnabled, isPro]
    );
    const [proLockedInfoCaseId, setProLockedInfoCaseId] = useState<string | null>(null);

    // Function to update URL parameters based on search filters (only for Received and Sent tabs, not My Team's Cases)
    const updateURLParams = useCallback((filters: typeof searchFilters) => {
        // Don't update URL params for My Team's Cases tab
        if (activeTab === 'myTeam') {
            return;
        }

        const newParams = new URLSearchParams();

        if (filters.lastName) newParams.set('lastName', filters.lastName);
        if (filters.firstName) newParams.set('firstName', filters.firstName);
        if (filters.notes) newParams.set('notes', filters.notes);
        if (filters.caseNumber) newParams.set('caseNumber', filters.caseNumber);
        if (filters.userId && filters.userId !== 'all') newParams.set('attorneyId', filters.userId);
        if (filters.updatedBy && filters.updatedBy !== 'all') newParams.set('updatedBy', filters.updatedBy);
        if (filters.updated && filters.updated !== 'all') newParams.set('updated', filters.updated);
        if (filters.includeArchived) newParams.set('includeArchived', 'true');
        if (filters.favoritesOnly) newParams.set('favoritesOnly', 'true');

        // Determine base path based on current location
        let basePath = '/cases/list';
        if (location.pathname === '/cases/received') {
            basePath = '/cases/received';
        } else if (location.pathname === '/cases/sent') {
            basePath = '/cases/sent';
        }
        const newURL = newParams.toString() ? `${basePath}?${newParams.toString()}` : basePath;
        navigate(newURL, { replace: true });
    }, [navigate, location.pathname, activeTab]);
    const [cases, setCases] = useState<CaseSummary[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [pendingReceivedCount, setPendingReceivedCount] = useState<number>(0);
    const [pendingSentCount, setPendingSentCount] = useState<number>(0);
    const [sentCases, setSentCases] = useState<CaseSentSummary[]>([]);
    const [loadingSentCases, setLoadingSentCases] = useState(false);
    const [errorSentCases, setErrorSentCases] = useState<string | null>(null);
    const [rescindingCaseSentId, setRescindingCaseSentId] = useState<string | null>(null);
    const [editingSentCaseNote, setEditingSentCaseNote] = useState<{ caseSentId: string; note: string } | null>(null);
    const [sortConfigSent, setSortConfigSent] = useState<{
        key: string;
        direction: 'asc' | 'desc';
    } | null>(null);
    const hasLoadedSentCases = useRef(false);
    const [receivedCases, setReceivedCases] = useState<CaseReceivedSummary[]>([]);
    const [loadingReceivedCases, setLoadingReceivedCases] = useState(false);
    const [errorReceivedCases, setErrorReceivedCases] = useState<string | null>(null);
    // Server verdict from list-received: receiving is Pro-only, so a non-Pro team gets a count only.
    const [receivedProRequired, setReceivedProRequired] = useState(false);
    const [sortConfigReceived, setSortConfigReceived] = useState<{
        key: string;
        direction: 'asc' | 'desc';
    } | null>(null);
    const hasLoadedReceivedCases = useRef(false);
    const [acceptingCaseSentId, setAcceptingCaseSentId] = useState<string | null>(null);
    const [rejectingCaseSentId, setRejectingCaseSentId] = useState<string | null>(null);
    const [updatingStatus, setUpdatingStatus] = useState<Set<string>>(new Set());
    const [updatingFavorites, setUpdatingFavorites] = useState<Set<string>>(new Set());
    const [checkingEditStatus, setCheckingEditStatus] = useState<Set<string>>(new Set());
    // In-flight case ids for the manual "clear my editing marker" action (per-row or bulk) --
    // see clearEditingMarkers below. ConcurrentCaseEditGuard is explicit-clear-only with no
    // automatic staleness/expiry (docs/AUTOSAVE_DETAILS.md §17), so this is the user's own
    // escape hatch for a marker left stale by a killed browser/crashed tab.
    const [clearingEditingMarkerIds, setClearingEditingMarkerIds] = useState<Set<string>>(new Set());
    const [showBulkClearEditingTooltip, setShowBulkClearEditingTooltip] = useState(false);
    const [updatingNotes, setUpdatingNotes] = useState<Set<string>>(new Set());
    const [showNoteModal, setShowNoteModal] = useState(false);
    const [editingCaseId, setEditingCaseId] = useState<string | null>(null);
    const [originalNoteValue, setOriginalNoteValue] = useState('');
    const noteInputRef = useRef<HTMLTextAreaElement>(null);
    const importFileInputRef = useRef<HTMLInputElement>(null);
    const [importingCase, setImportingCase] = useState(false);
    const [showExportModal, setShowExportModal] = useState(false);
    const [showExportZipModal, setShowExportZipModal] = useState(false);
    const [tooltipCaseId, setTooltipCaseId] = useState<string | null>(null);
    const [clickedTooltipCaseId, setClickedTooltipCaseId] = useState<string | null>(null);
    const [hoveredButton, setHoveredButton] = useState<{ caseId: string; button: string } | null>(null);
    const [hoveredNoteCaseId, setHoveredNoteCaseId] = useState<string | null>(null);
    const [isFlashing, setIsFlashing] = useState(false);
    const [previousCaseCount, setPreviousCaseCount] = useState<number | null>(null);

    // Tutorial state
    const [showTutorial, setShowTutorial] = useState(false);
    const [tutorialTargetElement, setTutorialTargetElement] = useState<HTMLElement | null>(null);
    const [tutorialStep, setTutorialStep] = useState(1);
    const [tutorialTitle, setTutorialTitle] = useState('');
    const [tutorialDescription, setTutorialDescription] = useState('');
    const [tutorialPosition, setTutorialPosition] = useState<'top' | 'bottom' | 'left' | 'right'>('bottom');

    // Announcement modal state
    const [showAnnouncementModal, setShowAnnouncementModal] = useState(false);

    // Check for unread announcements on mount
    useEffect(() => {
        if (!notificationsEnabled) return;

        const checkAnnouncements = async () => {
            try {
                const response = await notificationApiService.listNotifications({ unreadOnly: true, take: 500 });
                if (response.success) {
                    const hasUnread = (response.notifications ?? []).some(
                        n => !n.isRead && n.notificationType === 'announcement'
                    );
                    if (hasUnread) {
                        setShowAnnouncementModal(true);
                    }
                }
            } catch {
                // Silently fail - don't block homepage
            }
        };
        checkAnnouncements();
    }, [notificationsEnabled]);

    // Set page title based on route
    useEffect(() => {
        if (location.pathname === '/cases/received') {
            document.title = 'Received | Calculist';
        } else if (location.pathname === '/cases/sent') {
            document.title = 'Sent | Calculist';
        } else {
            document.title = 'Cases | Calculist';
        }
    }, [location.pathname]);

    // Tutorial handlers
    const handleTutorialClose = () => {
        setShowTutorial(false);
        setTutorialTargetElement(null);
        setTutorialStep(1);
        TutorialService.markFirstTimeTutorialAsDisplayed();
    };

    const handleTutorialNext = () => {
        if (tutorialStep === 1) {
            // Move to step 2 - star-only toggle
            setTutorialStep(2);
            setTutorialTitle("Filter by Favorite Cases");
            setTutorialDescription("Turn this on to only view your favorite items");
            setTutorialPosition('bottom');
            // Find the star-only toggle
            const starOnlyToggle = document.querySelector('[data-tutorial-target="favorite-only-toggle"]') as HTMLElement;
            if (starOnlyToggle) {
                setTutorialTargetElement(starOnlyToggle);
            }
        } else if (tutorialStep === 2) {
            // Move to step 3 - edit button
            setTutorialStep(3);
            setTutorialTitle("Edit or View Case");
            setTutorialDescription("Click the edit icon to open and modify a case. If the case is finalized, this will open in view-only mode.");
            setTutorialPosition('right');
            const editButton = document.querySelector('[data-tutorial-target="edit-button"]') as HTMLElement;
            if (editButton) {
                setTutorialTargetElement(editButton);
            }
        } else if (tutorialStep === 3) {
            // Move to step 4 - copy button
            setTutorialStep(4);
            setTutorialTitle("Copy Case");
            setTutorialDescription("Use the copy button to create a duplicate of an existing case with all its data.");
            setTutorialPosition('right');
            const copyButton = document.querySelector('[data-tutorial-target="copy-button"]') as HTMLElement;
            if (copyButton) {
                setTutorialTargetElement(copyButton);
            }
        } else if (tutorialStep === 4) {
            // Move to step 5 - download button
            setTutorialStep(5);
            setTutorialTitle("Download Documents");
            setTutorialDescription("Download PDF documents including summaries, worksheets, and financial affidavits for the case.");
            setTutorialPosition('right');
            const downloadButton = document.querySelector('[data-tutorial-target="download-button"]') as HTMLElement;
            if (downloadButton) {
                setTutorialTargetElement(downloadButton);
            }
        } else {
            // End tutorial
            handleTutorialClose();
        }
    };

    // Helper function to render parties with petitioner first
    const renderParties = (caseItem: CaseSummary) => {
        const partyA = caseItem.afn && caseItem.aln ? `${caseItem.afn} ${caseItem.aln}` : 'Party A';
        const partyB = caseItem.bfn && caseItem.bln ? `${caseItem.bfn} ${caseItem.bln}` : 'Party B';

        // Check if petitioner is Party B, otherwise default to Party A first
        const isPetitionerB = caseItem.pet === 'B';

        if (isPetitionerB) {
            // Show Party B first (petitioner), then Party A
            return (
                <div style={styles.partiesContainer}>
                    <div style={styles.partyA}>
                        {partyB}
                    </div>
                    <div style={styles.partyB}>
                        {partyA}
                    </div>
                </div>
            );
        } else {
            // Show Party A first (petitioner), then Party B
            return (
                <div style={styles.partiesContainer}>
                    <div style={styles.partyA}>
                        {partyA}
                    </div>
                    <div style={styles.partyB}>
                        {partyB}
                    </div>
                </div>
            );
        }
    };

    // Helper function to get user firstName from team.users by userId
    const getUserFirstName = (userId?: string): string => {
        if (!userId || !currentUser?.team?.users) {
            return 'Unknown';
        }
        const user = currentUser.team.users.find(u => u.userId === userId);
        return user?.firstName || 'Unknown';
    };

    // Helper function to get user full name from team.users by userId
    const getUserFullName = (userId?: string): string => {
        if (!userId || !currentUser?.team?.users) {
            return 'Unknown';
        }
        const user = currentUser.team.users.find(u => u.userId === userId);
        return user?.fullName || 'Unknown';
    };

    // Helper function to get "{firstName} {lastInitial}" from team.users by userId
    const getUserFirstNameLastInitial = (userId?: string): string => {
        if (!userId || !currentUser?.team?.users) {
            return 'Unknown';
        }
        const user = currentUser.team.users.find(u => u.userId === userId);
        if (!user?.firstName) {
            return 'Unknown';
        }
        return user.lastName ? `${user.firstName} ${user.lastName.charAt(0)}.` : user.firstName;
    };

    /** "{firstName} {lastInitial}." for the case's current editor. Prefers the name the backend
     * emits alongside edby (edbyfn/edbyln); falls back to resolving edby against the cached
     * team.users roster only when the server didn't supply a name (e.g. older cached response). */
    const editorFirstNameLastInitial = (caseItem: Pick<CaseSummary, 'edby' | 'edbyfn' | 'edbyln'>): string => {
        if (caseItem.edbyfn) {
            return caseItem.edbyln ? `${caseItem.edbyfn} ${caseItem.edbyln.charAt(0)}.` : caseItem.edbyfn;
        }
        return getUserFirstNameLastInitial(caseItem.edby);
    };

    /** Full "{firstName} {lastName}" for the case's current editor, preferring the server-emitted
     * name over the cached team.users roster lookup (see editorFirstNameLastInitial). */
    const editorFullName = (caseItem: Pick<CaseSummary, 'edby' | 'edbyfn' | 'edbyln'>): string => {
        if (caseItem.edbyfn) {
            return caseItem.edbyln ? `${caseItem.edbyfn} ${caseItem.edbyln}` : caseItem.edbyfn;
        }
        return getUserFullName(caseItem.edby);
    };

    /** True if the given editing-user GUID is the currently logged-in user (e.g. the case is open in another tab/browser). GUID compare is case-insensitive. */
    const isEditedByCurrentUser = (userId?: string): boolean => {
        if (!userId || !currentUser?.userId) {
            return false;
        }
        return userId.toLowerCase() === currentUser.userId.toLowerCase();
    };

    /** Attorney on case when listed in team.users; null if missing atid or user not on team. GUID compare is case-insensitive. */
    const resolveCaseAttorneyNameFromTeam = (atid?: string): string | null => {
        const id = atid?.toString().trim();
        if (!id || !currentUser?.team?.users) {
            return null;
        }
        const normalized = id.toLowerCase();
        const user = currentUser.team.users.find(u => u.userId.toLowerCase() === normalized);
        const raw = user?.fullName?.trim();
        if (!raw || raw.toLowerCase() === 'unknown') {
            return null;
        }
        return raw;
    };

    const getCaseAttorneySortLabel = (caseItem: CaseSummary): string =>
        resolveCaseAttorneyNameFromTeam(caseItem.atid) ?? 'Unspecified';

    const handleTutorialPrevious = () => {
        if (tutorialStep === 2) {
            // Go back to step 1 - star button
            setTutorialStep(1);
            setTutorialTitle("Star Cases to Mark as Favorites");
            setTutorialDescription("Click the star to mark cases you're interested in. Stars are personal to you and won't affect other team members.");
            setTutorialPosition('bottom');
            const firstStarButton = document.querySelector('[data-star-button]') as HTMLElement;
            if (firstStarButton) {
                setTutorialTargetElement(firstStarButton);
            }
        } else if (tutorialStep === 3) {
            // Go back to step 2 - star-only toggle
            setTutorialStep(2);
            setTutorialTitle("Filter by Favorite Cases");
            setTutorialDescription("Turn this on to only view your favorite items");
            setTutorialPosition('bottom');
            const starOnlyToggle = document.querySelector('[data-tutorial-target="favorite-only-toggle"]') as HTMLElement;
            if (starOnlyToggle) {
                setTutorialTargetElement(starOnlyToggle);
            }
        } else if (tutorialStep === 4) {
            // Go back to step 3 - edit button
            setTutorialStep(3);
            setTutorialTitle("Edit or View Case");
            setTutorialDescription("Click the edit icon to open and modify a case. If the case is finalized, this will open in view-only mode.");
            setTutorialPosition('right');
            const editButton = document.querySelector('[data-tutorial-target="edit-button"]') as HTMLElement;
            if (editButton) {
                setTutorialTargetElement(editButton);
            }
        } else if (tutorialStep === 5) {
            // Go back to step 4 - copy button
            setTutorialStep(4);
            setTutorialTitle("Copy Case");
            setTutorialDescription("Use the copy button to create a duplicate of an existing case with all its data.");
            setTutorialPosition('right');
            const copyButton = document.querySelector('[data-tutorial-target="copy-button"]') as HTMLElement;
            if (copyButton) {
                setTutorialTargetElement(copyButton);
            }
        }
    };

    const handleTutorialSkip = () => {
        // Skip tutorial and mark as viewed
        setShowTutorial(false);
        setTutorialTargetElement(null);
        setTutorialStep(1);
        TutorialService.markFirstTimeTutorialAsDisplayed();
    };

    // Temporary fallback styles for remaining unconverted elements
    const styles = {
        tableContainer: { border: `1px solid ${theme.colors.border.default}`, borderRadius: theme.borderRadius.md, overflow: 'visible' },
        table: { width: '100%', borderCollapse: 'collapse' as const },
        tableHeader: { backgroundColor: theme.colors.primary, color: theme.colors.text.inverse },
        headerCell: { padding: `${theme.spacing.sm} ${theme.spacing.md}`, textAlign: 'center' as const, fontSize: theme.typography.fontSize.lg, fontWeight: theme.typography.fontWeight.semibold, borderBottom: `1px solid ${theme.colors.border.default}`, whiteSpace: 'nowrap' as const },
        sortableHeader: { cursor: 'pointer', userSelect: 'none' as const, transition: `background-color ${theme.transitions.normal}` },
        tableRow: { borderBottom: `1px solid ${theme.colors.border.default}` },
        cell: { padding: `${theme.spacing.sm} ${theme.spacing.md}`, fontSize: theme.typography.fontSize.lg, color: theme.colors.text.secondary, verticalAlign: 'top' as const },
        dateCell: { padding: `${theme.spacing.sm} ${theme.spacing.md}`, fontSize: theme.typography.fontSize.lg, color: theme.colors.text.secondary, verticalAlign: 'top' as const, whiteSpace: 'pre-line' as const },
        printDropdownContainer: { position: 'relative' as const, display: 'inline-block' },
        printButton: { padding: theme.spacing.sm, backgroundColor: theme.colors.primary, border: `1px solid ${theme.colors.primary}`, borderRadius: theme.borderRadius.sm, cursor: 'pointer', fontSize: theme.typography.fontSize.sm, fontWeight: theme.typography.fontWeight.medium, color: theme.colors.text.inverse, display: 'flex', alignItems: 'center', justifyContent: 'center', transition: `all ${theme.transitions.normal}` },
        dropdownArrow: { fontSize: theme.typography.fontSize.xs, color: theme.colors.text.inverse },
        printDropdown: { position: 'absolute' as const, top: '100%', right: '0', backgroundColor: theme.colors.surface, border: `1px solid ${theme.colors.border.default}`, borderRadius: theme.borderRadius.md, boxShadow: theme.shadows.lg, zIndex: 9999, minWidth: '240px', overflow: 'visible' as const },
        printDropdownAbove: { top: 'auto', bottom: '100%', boxShadow: theme.shadows.md },
        printDropdownItem: { padding: `${theme.spacing.sm} ${theme.spacing.md}`, fontSize: theme.typography.fontSize.sm, color: theme.colors.text.primary, cursor: 'pointer', transition: `background-color ${theme.transitions.normal}`, backgroundColor: 'transparent' },
        printDropdownItemHover: { padding: `${theme.spacing.sm} ${theme.spacing.md}`, fontSize: theme.typography.fontSize.sm, color: theme.colors.text.inverse, cursor: 'pointer', transition: `background-color ${theme.transitions.normal}`, backgroundColor: theme.colors.primary },
        printDropdownSectionHeader: { padding: `${theme.spacing.sm} ${theme.spacing.md}`, fontSize: theme.typography.fontSize.sm, color: theme.colors.text.muted, fontWeight: theme.typography.fontWeight.semibold, textTransform: 'uppercase' as const, letterSpacing: '0.5px', borderBottom: `1px solid ${theme.colors.border.default}`, backgroundColor: theme.colors.surfaceSecondary, whiteSpace: 'nowrap' as const },
        actionButtons: { display: 'flex', gap: theme.spacing.xs, alignItems: 'center' },
        updateButton: {
            padding: theme.spacing.sm,
            backgroundColor: theme.colors.primary,
            border: `1px solid ${theme.colors.primary}`,
            borderRadius: theme.borderRadius.sm,
            cursor: 'pointer',
            fontSize: theme.typography.fontSize.sm,
            fontWeight: theme.typography.fontWeight.medium,
            color: theme.colors.text.inverse,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            transition: `all ${theme.transitions.normal}`,
            '&:hover': {
                backgroundColor: theme.colors.primaryHover,
                borderColor: theme.colors.primaryHover,
            }
        },
        copyButton: {
            padding: theme.spacing.sm,
            backgroundColor: theme.colors.primary,
            border: `1px solid ${theme.colors.primary}`,
            borderRadius: theme.borderRadius.sm,
            cursor: 'pointer',
            fontSize: theme.typography.fontSize.sm,
            fontWeight: theme.typography.fontWeight.medium,
            color: theme.colors.text.inverse,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            transition: `all ${theme.transitions.normal}`,
            '&:hover': {
                backgroundColor: theme.colors.primaryHover,
                borderColor: theme.colors.primaryHover,
            }
        },
        buttonIcon: { fontSize: theme.typography.fontSize.md, color: theme.colors.text.inverse },
        buttonLabel: { fontSize: theme.typography.fontSize.sm, fontWeight: theme.typography.fontWeight.medium, color: theme.colors.text.inverse },
        partyNames: { fontSize: theme.typography.fontSize.lg, color: theme.colors.text.primary },
        partiesContainer: { display: 'flex', flexDirection: 'column' as const, gap: theme.spacing.xs },
        partyA: { fontSize: theme.typography.fontSize.lg, color: theme.colors.text.primary, fontWeight: theme.typography.fontWeight.medium },
        partyB: { fontSize: theme.typography.fontSize.lg, color: theme.colors.text.muted, fontStyle: 'italic' },
        notesContainer: { position: 'relative' as const, display: 'flex', alignItems: 'center', gap: theme.spacing.sm, cursor: 'pointer', padding: theme.spacing.xs, borderRadius: theme.borderRadius.sm, transition: `background-color ${theme.transitions.normal}` },
        editIcon: { fontSize: theme.typography.fontSize.xxl, color: theme.colors.text.muted },
        notesBox: { backgroundColor: '#dbeafe', padding: `${theme.spacing.xs} ${theme.spacing.sm}`, borderRadius: theme.borderRadius.xl, fontSize: theme.typography.fontSize.sm, color: '#1e40af', maxWidth: '200px', wordWrap: 'break-word' as const, whiteSpace: 'normal' as const },
        statusSelect: { padding: `${theme.spacing.xs} ${theme.spacing.sm}`, border: `1px solid ${theme.colors.border.default}`, borderRadius: theme.borderRadius.sm, fontSize: theme.typography.fontSize.lg, backgroundColor: theme.colors.surface },
        loadingMessage: { padding: theme.spacing.xl, textAlign: 'center' as const, color: theme.colors.text.muted, fontSize: theme.typography.fontSize.lg, display: 'flex', flexDirection: 'column' as const, alignItems: 'center', gap: theme.spacing.md },
        loadingSpinner: { width: '48px', height: '48px' },
        errorMessage: { padding: theme.spacing.xl, textAlign: 'center' as const, color: theme.colors.status.error, fontSize: theme.typography.fontSize.lg },
        modalOverlay: { position: 'fixed' as const, top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0, 0, 0, 0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 },
        modalContent: { backgroundColor: theme.colors.surface, borderRadius: theme.borderRadius.lg, padding: '0', width: '50vw', maxWidth: '600px', boxShadow: theme.shadows.lg },
        modalHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: `${theme.spacing.lg} ${theme.spacing.lg}`, borderBottom: `1px solid ${theme.colors.border.default}`, backgroundColor: theme.colors.primary },
        modalTitle: { margin: 0, fontSize: theme.typography.fontSize.xl, fontWeight: theme.typography.fontWeight.semibold, color: theme.colors.text.inverse },
        modalCloseButton: { background: 'none', border: 'none', fontSize: theme.typography.fontSize.xxl, color: theme.colors.text.inverse, cursor: 'pointer', padding: '0', width: '32px', height: '32px', display: 'flex', alignItems: 'center', justifyContent: 'center', borderRadius: theme.borderRadius.sm },
        modalBody: { padding: theme.spacing.lg, display: 'flex', flexDirection: 'column' as const, gap: theme.spacing.lg },
        modalText: { margin: 0, fontSize: theme.typography.fontSize.lg, color: theme.colors.text.secondary, textAlign: 'center' as const },
        modalFooter: { display: 'flex', justifyContent: 'flex-end', gap: theme.spacing.sm, padding: `${theme.spacing.md} ${theme.spacing.lg}`, borderTop: `1px solid ${theme.colors.border.default}` },
        modalButton: { padding: `${theme.spacing.sm} ${theme.spacing.md}`, borderRadius: theme.borderRadius.sm, fontSize: theme.typography.fontSize.md, fontWeight: theme.typography.fontWeight.medium, cursor: 'pointer', border: 'none', transition: `all ${theme.transitions.normal}` },
        tbdMessage: { padding: `${theme.spacing.xxl} ${theme.spacing.xl}`, textAlign: 'center' as const, backgroundColor: theme.colors.surfaceSecondary },
        tbdText: { margin: 0, fontSize: theme.typography.fontSize.xxl, color: theme.colors.text.muted, fontWeight: theme.typography.fontWeight.medium },
        paginationContainer: { display: 'flex', justifyContent: 'center', alignItems: 'center', gap: theme.spacing.sm, marginTop: theme.spacing.md, padding: `${theme.spacing.md} 0` },
        paginationButton: { padding: `${theme.spacing.sm} ${theme.spacing.md}`, border: `1px solid ${theme.colors.border.default}`, borderRadius: theme.borderRadius.sm, fontSize: theme.typography.fontSize.sm, fontWeight: theme.typography.fontWeight.medium, cursor: 'pointer', transition: `all ${theme.transitions.normal}` },
        paginationButtonEnabled: { backgroundColor: theme.colors.surface, color: theme.colors.primary, borderColor: theme.colors.border.default },
        paginationButtonDisabled: { backgroundColor: theme.colors.surfaceSecondary, color: theme.colors.status.disabled, borderColor: theme.colors.border.default, cursor: 'not-allowed' },
        pageNumbers: { display: 'flex', alignItems: 'center', gap: theme.spacing.xs },
        pageNumber: { padding: `${theme.spacing.sm} ${theme.spacing.sm}`, border: `1px solid ${theme.colors.border.default}`, borderRadius: theme.borderRadius.sm, fontSize: theme.typography.fontSize.sm, fontWeight: theme.typography.fontWeight.medium, backgroundColor: theme.colors.surface, color: theme.colors.text.muted, cursor: 'pointer', transition: `all ${theme.transitions.normal}`, minWidth: '40px' },
        pageNumberActive: { backgroundColor: theme.colors.primary, color: theme.colors.text.inverse, borderColor: theme.colors.primary },
        ellipsis: { padding: `${theme.spacing.sm} ${theme.spacing.xs}`, fontSize: theme.typography.fontSize.sm, color: theme.colors.text.muted },
    };

    // Search and filter state - initialize with defaults
    const [searchFilters, setSearchFilters] = useState({
        lastName: '',
        firstName: '',
        notes: '',
        caseNumber: '',
        userId: 'all',
        updatedBy: 'all',
        updated: 'all',
        includeArchived: false,
        favoritesOnly: false,
    });

    const [sortConfig, setSortConfig] = useState<{
        key: string;
        direction: 'asc' | 'desc';
    } | null>(null);

    // Debounced search state - initialize with defaults (My Team's Cases doesn't use URL params)
    const [debouncedFilters, setDebouncedFilters] = useState({
        lastName: '',
        firstName: '',
        notes: '',
        caseNumber: '',
        userId: 'all',
        updatedBy: 'all',
        updated: 'all',
        includeArchived: false,
        favoritesOnly: false,
    });

    // Get auth loading state
    const { loading: authLoading } = useAuthContext();

    // Attorney options for react-select
    const attorneyOptions = useMemo(() => {
        const inactiveSuffix = (status?: string) =>
            status?.toLowerCase() === 'inactive' ? ' (Inactive)' : '';
        const isInactiveStatus = (status?: string) => status?.toLowerCase() === 'inactive';

        const options: Array<{ value: string; label: string; isInactive?: boolean }> = [
            { value: 'all', label: 'All' }
        ];

        // Add "Me" option if current user is an attorney
        if (currentUser?.userId && currentUser?.businessRole?.toLowerCase() === 'attorney') {
            const inactive = isInactiveStatus(currentUser.status);
            options.push({
                value: currentUser.userId,
                label: `Me${inactiveSuffix(currentUser.status)}`,
                ...(inactive ? { isInactive: true } : {}),
            });
        }

        // Add other team users who are attorneys
        if (currentUser?.team?.users) {
            currentUser.team.users
                .filter(teamUser =>
                    teamUser.userId !== currentUser.userId &&
                    teamUser.businessRole?.toLowerCase() === 'attorney'
                )
                .forEach(teamUser => {
                    const inactive = isInactiveStatus(teamUser.status);
                    options.push({
                        value: teamUser.userId,
                        label: `${teamUser.fullName}${inactiveSuffix(teamUser.status)}`,
                        ...(inactive ? { isInactive: true } : {}),
                    });
                });
        }

        return options;
    }, [currentUser]);

    // Updated By options for react-select (same as attorney options - all team users)
    const updatedByOptions = useMemo(() => {
        const options = [
            { value: 'all', label: 'Anybody' }
        ];

        // Add all team users
        if (currentUser?.team?.users) {
            currentUser.team.users.forEach(teamUser => {
                options.push({
                    value: teamUser.userId,
                    label: teamUser.userId === currentUser?.userId ? 'Me' : teamUser.fullName
                });
            });
        }

        return options;
    }, [currentUser]);

    // Updated time options for react-select
    const updatedTimeOptions = useMemo(() => [
        { value: 'all', label: 'Any Time' },
        { value: '60_minutes', label: 'One Hour' },
        { value: 'today', label: 'Today' },
        { value: '3_days', label: '3 Days' },
        { value: '7_days', label: '7 Days' },
        { value: '14_days', label: '14 Days' },
        { value: '30_days', label: '30 Days' },
        { value: '90_days', label: '90 Days' },
        { value: '365_days', label: '365 Days' },
    ], []);

    // Default filter values: Attorney = "Me" if current user is attorney, else "All"
    const defaultFilters = useMemo(() => ({
        lastName: '',
        firstName: '',
        notes: '',
        caseNumber: '',
        userId: (currentUser?.userId && currentUser?.businessRole?.toLowerCase() === 'attorney') ? currentUser.userId : 'all',
        updatedBy: 'all',
        updated: 'all',
        includeArchived: false,
        favoritesOnly: false,
    }), [currentUser?.userId, currentUser?.businessRole]);

    const hasNonDefaultFilter = useMemo(() => {
        return searchFilters.lastName !== defaultFilters.lastName
            || searchFilters.firstName !== defaultFilters.firstName
            || searchFilters.notes !== defaultFilters.notes
            || searchFilters.caseNumber !== defaultFilters.caseNumber
            || searchFilters.userId !== defaultFilters.userId
            || searchFilters.updatedBy !== defaultFilters.updatedBy
            || searchFilters.updated !== defaultFilters.updated
            || searchFilters.includeArchived !== defaultFilters.includeArchived
            || searchFilters.favoritesOnly !== defaultFilters.favoritesOnly;
    }, [searchFilters, defaultFilters]);

    // Case ids currently loaded whose "editing" marker is the CURRENT user's own -- the
    // candidate set for the manual "clear my editing marks" bulk action (see
    // clearEditingMarkers above). Never includes a teammate's marker.
    const myStaleEditingCaseIds = useMemo(() => {
        return cases.filter(c => isEditedByCurrentUser(c.edby) && c.cid).map(c => c.cid as string);
    }, [cases]);

    // Helper function to validate and sanitize saved filters
    // Ensures userId and updatedBy values exist in their respective options
    const validateAndSanitizeFilters = useCallback((filters: any) => {
        const sanitized = { ...filters };

        // Validate userId - must be 'all' or exist in attorneyOptions
        if (sanitized.userId && sanitized.userId !== 'all') {
            const isValidUserId = attorneyOptions.some(option => option.value === sanitized.userId);
            if (!isValidUserId) {
                sanitized.userId = 'all';
            }
        }

        // Validate updatedBy - must be 'all' or exist in updatedByOptions
        if (sanitized.updatedBy && sanitized.updatedBy !== 'all') {
            const isValidUpdatedBy = updatedByOptions.some(option => option.value === sanitized.updatedBy);
            if (!isValidUpdatedBy) {
                sanitized.updatedBy = 'all';
            }
        }

        // Validate updated - must be 'all' or exist in updatedTimeOptions
        if (sanitized.updated && sanitized.updated !== 'all') {
            const isValidUpdated = updatedTimeOptions.some(option => option.value === sanitized.updated);
            if (!isValidUpdated) {
                sanitized.updated = 'all';
            }
        }

        return sanitized;
    }, [attorneyOptions, updatedByOptions, updatedTimeOptions]);

    // Status options with theme colors
    const statusOptions = useMemo(() => [
        { value: 'working', label: 'Working', color: theme.colors.primary, barColor: theme.colors.primary },
        { value: 'finalized', label: 'Finalized', color: theme.colors.secondary, barColor: theme.colors.secondary },
        { value: 'archived', label: 'Archived', color: '#8b5cf6', barColor: '#8b5cf6' }, // purple - archived but not disabled
    ], [theme.colors.primary, theme.colors.secondary]);

    // Track if we've initialized the userId filter
    const hasInitializedUserId = useRef(false);

    // Track if we've restored from localStorage to avoid conflicts with URL params
    const hasRestoredReceivedSort = useRef(false);
    const hasRestoredSentSort = useRef(false);


    // Helper function to get user-specific localStorage key
    const getUserStorageKey = useCallback((key: string): string => {
        const userId = currentUser?.userId || 'anonymous';
        return `${key}_${userId}`;
    }, [currentUser?.userId]);

    // CaseStateManager removed - using simple architecture
    // useEffect(() => {
    //     CaseStateManager.initialize();

    // CaseStateManager removed - using simple architecture
    // const handleOtherCaseUpdate = (data: any) => {
    //     setCases(prev => prev.map(caseItem =>
    //         caseItem.cid === data.caseId
    //             ? { ...caseItem, ...data.updates, etag: data.etag }
    //             : caseItem
    //     ));
    // };

    // const handleOtherCaseNoteChange = (data: any) => {
    //     setCases(prev => prev.map(caseItem =>
    //         caseItem.cid === data.caseId
    //             ? { ...caseItem, n: data.note, etag: data.etag }
    //             : caseItem
    //     ));
    // };

    // const handleOtherCaseStatusChange = (data: any) => {
    //     setCases(prev => prev.map(caseItem =>
    //         caseItem.cid === data.caseId
    //             ? { ...caseItem, stat: data.status, etag: data.etag }
    //             : caseItem
    //     ));
    // };

    // CaseStateManager.on(CaseEvents.OTHER_CASE_UPDATED, handleOtherCaseUpdate);
    // CaseStateManager.on(CaseEvents.OTHER_CASE_NOTE_CHANGED, handleOtherCaseNoteChange);
    // CaseStateManager.on(CaseEvents.OTHER_CASE_STATUS_CHANGED, handleOtherCaseStatusChange);

    // return () => {
    //     CaseStateManager.off(CaseEvents.OTHER_CASE_UPDATED, handleOtherCaseUpdate);
    //     CaseStateManager.off(CaseEvents.OTHER_CASE_NOTE_CHANGED, handleOtherCaseNoteChange);
    //     CaseStateManager.off(CaseEvents.OTHER_CASE_STATUS_CHANGED, handleOtherCaseStatusChange);
    // };
    // }, []);

    // Modal state
    const [editGuardCase, setEditGuardCase] = useState<CaseSummary | null>(null);
    // Deferred action awaiting a "someone else is editing -- proceed anyway?" confirmation
    // (ConcurrentCaseEditGuard awareness for homepage note/status mutations). See useCaseEditGuard.
    const { guardedAction, setGuardedAction, runWithEditGuard } = useCaseEditGuard(concurrentCaseEditGuardEnabled, currentUser?.userId);
    const [showCopyModal, setShowCopyModal] = useState(false);
    const [copyModalCaseId, setCopyModalCaseId] = useState<string | null>(null);
    const [showDownloadModal, setShowDownloadModal] = useState(false);
    const [downloadModalCaseId, setDownloadModalCaseId] = useState<string | null>(null);
    const [downloadModalCaseData, setDownloadModalCaseData] = useState<CaseData | null>(null);
    const [showDownloadNotAvailableModal, setShowDownloadNotAvailableModal] = useState(false);
    const [pdfGenerating, setPdfGenerating] = useState(false);
    const [currentPdfDocument, setCurrentPdfDocument] = useState<string>('');
    const [pdfSpinnerAiAssisted, setPdfSpinnerAiAssisted] = useState(false);
    const [pdfSpinnerMessage, setPdfSpinnerMessage] = useState('');
    const allocationPdfProgressIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
    const [showRescindModal, setShowRescindModal] = useState(false);
    const [rescindModalCaseSentId, setRescindModalCaseSentId] = useState<string | null>(null);
    const [showRescindRecipientsModal, setShowRescindRecipientsModal] = useState(false);
    const [showRescindErrorModal, setShowRescindErrorModal] = useState(false);
    const [rescindErrorMessage, setRescindErrorMessage] = useState<string>('');
    const [showAcceptErrorModal, setShowAcceptErrorModal] = useState(false);
    const [acceptErrorMessage, setAcceptErrorMessage] = useState<string>('');
    const [showAcceptCaseModal, setShowAcceptCaseModal] = useState(false);
    const [acceptModalCase, setAcceptModalCase] = useState<CaseReceivedSummary | null>(null);
    const [showAcceptSuccessModal, setShowAcceptSuccessModal] = useState(false);
    const [acceptedCaseId, setAcceptedCaseId] = useState<string | null>(null);
    const [showProUpgradeFromUpsellModal, setShowProUpgradeFromUpsellModal] = useState(false);
    const [showHighIncomePdfProModal, setShowHighIncomePdfProModal] = useState(false);
    const [showCalculonAllocationPdfProModal, setShowCalculonAllocationPdfProModal] = useState(false);
    const [showAlimonyAnalysisPdfProModal, setShowAlimonyAnalysisPdfProModal] = useState(false);
    const [showAlimonyScenarioModal, setShowAlimonyScenarioModal] = useState(false);
    // The picker needs the case after the download modal has released it.
    const [alimonyScenarioCase, setAlimonyScenarioCase] = useState<{ caseId: string; caseData: CaseData | null } | null>(null);
    const [showWordDocProModal, setShowWordDocProModal] = useState(false);

    // Pagination state
    const [currentPage, setCurrentPage] = useState(1);
    const pageSize = 25;
    const [currentPageSent, setCurrentPageSent] = useState(1);
    const pageSizeSent = 25;
    const [currentPageReceived, setCurrentPageReceived] = useState(1);
    const pageSizeReceived = 25;
    const [openStatusDropdown, setOpenStatusDropdown] = useState<string | null>(null);


    // Tutorial initialization
    useEffect(() => {
        // Check if this is the first time user and show tutorial
        if (!TutorialService.hasDisplayedFirstTimeTutorial()) {
            // Wait for the component to render and find the first star button
            const timer = setTimeout(() => {
                const firstStarButton = document.querySelector('[data-star-button]') as HTMLElement;
                if (firstStarButton) {
                    setTutorialTargetElement(firstStarButton);
                    setTutorialTitle("Star Cases to Mark as Favorites");
                    setTutorialDescription("Click the star to mark cases you're interested in. Stars are personal to you and won't affect other team members.");
                    setTutorialPosition('bottom');
                    setShowTutorial(true);
                }
            }, 1000); // Wait 1 second for the page to load

            return () => clearTimeout(timer);
        }
    }, [cases]); // Re-run when cases are loaded

    // Define loadCases before useEffect that uses it
    // `quiet` skips the full-page loading spinner -- used by background refreshes (the live
    // editing-presence poll and the tab-return refresh) so re-pulling the list every ~30s to
    // keep the "who's editing" verbiage current doesn't flash the spinner over the table.
    const loadCases = useCallback(async (quiet = false) => {
        try {
            if (!quiet) setLoading(true);
            setError(null);
            const response = await caseApiService.getCases();
            if (response.data.success) {
                setCases(response.data.cases);
                setPendingReceivedCount(response.data.pendingReceivedCount ?? 0);
                setPendingSentCount(response.data.pendingSentCount ?? 0);
            } else {
                setError(response.data.message || 'Failed to load cases');
            }
        } catch (err) {
            setError('Failed to load cases');
            debugError('Error loading cases:', err);
        } finally {
            if (!quiet) setLoading(false);
        }
    }, []); // No dependencies - uses stable setters and service

    /**
     * Manually clears the "currently editing" marker for one or more of the CURRENT user's own
     * cases (ConcurrentCaseEditGuard). The backend (`ClearCaseEditingAsync`) only clears the
     * marker when it still matches the calling user's own id, so this can never dismiss a
     * teammate's genuinely-active marker -- it's scoped to "my own stale marker" by construction.
     * Re-runs loadCases() afterward, which both refreshes the UI and verifies the clear actually
     * took (a real second tab that re-marked the case in the interim will show that instead).
     */
    const clearEditingMarkers = useCallback(async (caseIds: string[]) => {
        if (caseIds.length === 0) return;
        setClearingEditingMarkerIds(prev => new Set([...prev, ...caseIds]));
        try {
            await Promise.all(caseIds.map(id => caseApiService.clearEditing(id)));
            await loadCases();
            addAlert('success', `Cleared the "editing" marker on ${caseIds.length} case${caseIds.length === 1 ? '' : 's'}.`);
        } catch (err) {
            debugError('Error clearing editing markers:', err);
            addAlert('error', 'Failed to clear editing markers. Please try again.');
        } finally {
            setClearingEditingMarkerIds(prev => {
                const next = new Set(prev);
                caseIds.forEach(id => next.delete(id));
                return next;
            });
        }
    }, [loadCases, addAlert]);

    // Track previous pathname to detect navigation from wizard pages
    const prevPathnameRef = useRef<string>(location.pathname);

    // Load cases on component mount and when navigating back to homepage
    // This ensures fresh data when returning from other pages (e.g., settings, wizard)
    useEffect(() => {
        // Only load for "myTeam" tab (not for sent/received tabs)
        if (activeTab === 'myTeam') {
            const wasWizardPage = prevPathnameRef.current?.includes('/wizard/');
            const isHomePage = location.pathname === '/cases/list' || location.pathname === '/';

            // Always load cases when pathname changes
            // Note: For normal navigation, useNavigationGuard calls forceSave() before navigation,
            // which cancels any pending debounced auto-saves and completes the save before navigating.
            // So the immediate refresh should be sufficient for most cases.
            loadCases();

            // If coming from wizard, add a single delayed refresh as a safety net
            // This handles the edge case where SimpleCaseContext's location change effect
            // saves asynchronously without awaiting, so navigation might complete slightly
            // before the save finishes. The delay ensures we catch any late saves.
            if (wasWizardPage && isHomePage) {
                const delayedRefresh = setTimeout(() => {
                    loadCases();
                }, 750); // 750ms delay to catch async location change effect saves

                return () => clearTimeout(delayedRefresh);
            }
        }

        prevPathnameRef.current = location.pathname;
    }, [location.pathname, activeTab, loadCases]); // Refresh when pathname changes (e.g., navigating back from wizard)

    // Refresh cases when page becomes visible (user returns to tab/window)
    // This is a backup mechanism to catch edge cases:
    // - User switching tabs and making changes in another tab
    // - Direct URL navigation that might bypass navigation guards
    // Uses a longer minimum interval to avoid excessive refreshes
    useEffect(() => {
        if (activeTab !== 'myTeam') return;

        let lastRefreshTime = 0;
        const MIN_REFRESH_INTERVAL = 5000; // Don't refresh more than once per 5 seconds

        const handleVisibilityChange = () => {
            // Only refresh if page is visible and enough time has passed since last refresh
            if (!document.hidden && activeTab === 'myTeam') {
                const now = Date.now();
                if (now - lastRefreshTime > MIN_REFRESH_INTERVAL) {
                    lastRefreshTime = now;
                    loadCases(true); // quiet: no spinner flash on tab-return refresh
                }
            }
        };

        document.addEventListener('visibilitychange', handleVisibilityChange);

        return () => {
            document.removeEventListener('visibilitychange', handleVisibilityChange);
        };
    }, [activeTab, loadCases]);

    // Live editing presence: while sitting on the "myTeam" list with the flag on, quietly
    // re-pull the list every 30s (visibility-gated -- paused entirely when the tab is hidden)
    // so the "X is editing"/"You are editing" verbiage stays current as teammates start/stop
    // editing. The tab-return refresh above already covers re-focus, so runOnVisible is off
    // here. See docs/AUTOSAVE_DETAILS.md §17.
    useVisibilityAwarePolling(
        () => loadCases(true),
        30000,
        { enabled: concurrentCaseEditGuardEnabled && activeTab === 'myTeam', runOnVisible: false }
    );

    // Same-browser fast-path over the presence channel (see AUTOSAVE_DETAILS §17):
    //  - 'takeover'/'released' (editing started/stopped): the editor/name changed, so do a
    //    debounced quiet reload to pull fresh edby/edbytab/names.
    //  - 'saved': only the case's updated_at changed -- patch that row's `upat` locally so the
    //    "last saved X ago" refreshes instantly with zero server calls (we already have the new
    //    timestamp in the broadcast).
    useEffect(() => {
        if (!concurrentCaseEditGuardEnabled || activeTab !== 'myTeam') return;
        let debounce: ReturnType<typeof setTimeout> | null = null;
        const unsubscribe = subscribeToPresence((msg) => {
            if (msg.type === 'saved') {
                if (msg.updatedAt) {
                    setCases(prev => prev.map(c => (c.cid === msg.caseId ? { ...c, upat: msg.updatedAt as string } : c)));
                }
                return;
            }
            if (debounce) clearTimeout(debounce);
            debounce = setTimeout(() => loadCases(true), 500);
        });
        return () => {
            if (debounce) clearTimeout(debounce);
            unsubscribe();
        };
    }, [concurrentCaseEditGuardEnabled, activeTab, loadCases]);

    // Load sent cases when Sent tab is activated (if Pro user) - only load once
    useEffect(() => {
        if (isPro && activeTab === 'sent' && !hasLoadedSentCases.current) {
            loadSentCases();
            hasLoadedSentCases.current = true;
        }
    }, [isPro, activeTab]);

    // Load received cases when Received tab is activated - only load once
    useEffect(() => {
        if (activeTab === 'received' && !hasLoadedReceivedCases.current) {
            loadReceivedCases();
            hasLoadedReceivedCases.current = true;
        }
    }, [activeTab]);

    // Close status dropdown when clicking outside
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            const target = event.target as HTMLElement;
            if (!target.closest('[data-status-dropdown]')) {
                setOpenStatusDropdown(null);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, []);

    // Note: Download modal closes on overlay click, no need for document click handler


    // Initialize filters and sort from localStorage when user becomes available
    useEffect(() => {
        if (!currentUser?.userId) return;

        // Initialize My Team's Cases filters and sort
        const rememberMyTeamFilters = localStorage.getItem('rememberMyTeamFilters') !== 'false';
        if (rememberMyTeamFilters && activeTab === 'myTeam') {
            const savedFilters = localStorage.getItem('savedMyTeamFilters');
            if (savedFilters) {
                try {
                    const filters = JSON.parse(savedFilters);
                    const validatedFilters = validateAndSanitizeFilters(filters);
                    setSearchFilters(validatedFilters);
                    // Immediately sync debouncedFilters to avoid race condition
                    setDebouncedFilters(validatedFilters);
                } catch (e) {
                    console.error('Error parsing saved My Team filters:', e);
                }
            }

            const savedSort = localStorage.getItem('savedMyTeamSort');
            if (savedSort) {
                try {
                    const sort = JSON.parse(savedSort);
                    setSortConfig(sort);
                } catch (e) {
                    console.error('Error parsing saved My Team sort:', e);
                }
            }
        }

        // Initialize Received sort
        const rememberReceivedSort = localStorage.getItem('rememberReceivedSort') !== 'false';
        if (rememberReceivedSort && activeTab === 'received') {
            const savedSort = localStorage.getItem('savedReceivedSort');
            if (savedSort) {
                try {
                    const sort = JSON.parse(savedSort);
                    setSortConfigReceived(sort);
                } catch (e) {
                    console.error('Error parsing saved Received sort:', e);
                }
            }
        }

        // Initialize Sent sort
        const rememberSentSort = localStorage.getItem('rememberSentSort') !== 'false';
        if (rememberSentSort && activeTab === 'sent') {
            const savedSort = localStorage.getItem('savedSentSort');
            if (savedSort) {
                try {
                    const sort = JSON.parse(savedSort);
                    setSortConfigSent(sort);
                } catch (e) {
                    console.error('Error parsing saved Sent sort:', e);
                }
            }
        }

        // Mark userId as initialized - "All" should stay as 'all' to show all cases
        if (activeTab === 'myTeam' && !hasInitializedUserId.current) {
            hasInitializedUserId.current = true;
        }
    }, [currentUser?.userId, activeTab, validateAndSanitizeFilters]);


    // Restore filters/sort from localStorage when tab changes
    useEffect(() => {
        // Restore My Team's Cases filters and sort when switching to that tab
        if (activeTab === 'myTeam') {
            const rememberMyTeamFilters = localStorage.getItem('rememberMyTeamFilters') !== 'false';
            if (rememberMyTeamFilters) {
                const savedFilters = localStorage.getItem('savedMyTeamFilters');
                if (savedFilters) {
                    try {
                        const filters = JSON.parse(savedFilters);
                        const validatedFilters = validateAndSanitizeFilters(filters);
                        setSearchFilters(validatedFilters);
                        // Immediately sync debouncedFilters to avoid race condition
                        setDebouncedFilters(validatedFilters);
                    } catch (e) {
                        console.error('Error parsing saved My Team filters:', e);
                    }
                }

                const savedSort = localStorage.getItem('savedMyTeamSort');
                if (savedSort) {
                    try {
                        const sort = JSON.parse(savedSort);
                        setSortConfig(sort);
                    } catch (e) {
                        console.error('Error parsing saved My Team sort:', e);
                    }
                }
            }
        }

        // Restore Received sort when switching to that tab
        if (activeTab === 'received' && !hasRestoredReceivedSort.current) {
            const rememberReceivedSort = localStorage.getItem('rememberReceivedSort') !== 'false';
            if (rememberReceivedSort) {
                const savedSort = localStorage.getItem('savedReceivedSort');
                if (savedSort) {
                    try {
                        const sort = JSON.parse(savedSort);
                        setSortConfigReceived(sort);
                    } catch (e) {
                        console.error('Error parsing saved Received sort:', e);
                    }
                }
            }
            hasRestoredReceivedSort.current = true;
        } else if (activeTab !== 'received') {
            hasRestoredReceivedSort.current = false;
        }

        // Restore Sent sort when switching to that tab
        if (activeTab === 'sent' && !hasRestoredSentSort.current) {
            const rememberSentSort = localStorage.getItem('rememberSentSort') !== 'false';
            if (rememberSentSort) {
                const savedSort = localStorage.getItem('savedSentSort');
                if (savedSort) {
                    try {
                        const sort = JSON.parse(savedSort);
                        setSortConfigSent(sort);
                    } catch (e) {
                        console.error('Error parsing saved Sent sort:', e);
                    }
                }
            }
            hasRestoredSentSort.current = true;
        } else if (activeTab !== 'sent') {
            hasRestoredSentSort.current = false;
        }
    }, [activeTab, validateAndSanitizeFilters]); // Run when tab changes


    // Validate and sync filters when attorneyOptions change (e.g., when currentUser loads)
    // This ensures filters are valid when options become available
    useEffect(() => {
        if (!currentUser?.userId || activeTab !== 'myTeam') return;

        // Validate current filters against available options
        const validatedFilters = validateAndSanitizeFilters(searchFilters);

        // Only update if validation changed something (e.g., invalid userId was reset to 'all')
        if (validatedFilters.userId !== searchFilters.userId ||
            validatedFilters.updatedBy !== searchFilters.updatedBy ||
            validatedFilters.updated !== searchFilters.updated) {
            setSearchFilters(validatedFilters);
            setDebouncedFilters(validatedFilters);
        }
    }, [attorneyOptions, updatedByOptions, currentUser?.userId, activeTab, validateAndSanitizeFilters]);

    // Debounce search filters
    useEffect(() => {
        const timer = setTimeout(() => {
            setDebouncedFilters(searchFilters);
        }, 300);

        return () => clearTimeout(timer);
    }, [searchFilters]);

    // Update URL parameters when debounced filters change
    useEffect(() => {
        updateURLParams(debouncedFilters);
    }, [debouncedFilters, updateURLParams]);

    // Close tooltip when clicking outside (only for click-based tooltips)
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (tooltipCaseId) {
                const target = event.target as Element;
                // Don't close if clicking on the info icon itself
                if (!target.closest('[data-info-icon]')) {
                    setTooltipCaseId(null);
                    setClickedTooltipCaseId(null);
                }
            }
        };

        if (tooltipCaseId) {
            document.addEventListener('click', handleClickOutside);
            return () => {
                document.removeEventListener('click', handleClickOutside);
            };
        }
    }, [tooltipCaseId]);

    const loadSentCases = async (forceReload: boolean = false) => {
        // Don't reload if already loaded (unless forced)
        if (!forceReload && sentCases.length > 0 && !loadingSentCases) {
            return;
        }
        try {
            setLoadingSentCases(true);
            setErrorSentCases(null);
            const response = await caseApiService.getSentCases();
            if (response.data?.success) {
                const loadedSentCases = response.data.sentCases || [];
                setSentCases(loadedSentCases);

                // Count pending sent cases (cases with status 'Pending' or 'Mixed', or at least one recipient with pending status)
                const pendingCount = loadedSentCases.filter(sentCase => {
                    // Check if the case itself is pending or mixed (mixed means some recipients are pending)
                    if (sentCase.status === 'Pending' || sentCase.status === 'Mixed') {
                        return true;
                    }
                    // Check if any recipient has pending status
                    return sentCase.sentTo?.some(recipient => recipient.status === 'Pending') ?? false;
                }).length;
                setPendingSentCount(pendingCount);
            } else {
                setErrorSentCases(response.data?.message || 'Failed to load sent cases');
            }
        } catch (err) {
            setErrorSentCases('Failed to load sent cases');
            debugError('Error loading sent cases:', err);
        } finally {
            setLoadingSentCases(false);
        }
    };

    const loadReceivedCases = async (forceReload: boolean = false) => {
        // Don't reload if already loaded (unless forced)
        if (!forceReload && receivedCases.length > 0 && !loadingReceivedCases) {
            return;
        }
        try {
            setLoadingReceivedCases(true);
            setErrorReceivedCases(null);
            const response = await caseApiService.getReceivedCases();
            if (response.data?.success && response.data.proRequired) {
                setReceivedProRequired(true);
                setReceivedCases([]);
                setPendingReceivedCount(response.data.pendingCount ?? 0);
            } else if (response.data?.success) {
                setReceivedProRequired(false);
                const loadedReceivedCases = response.data.receivedCases || [];
                setReceivedCases(loadedReceivedCases);

                // Count pending received cases (cases with status 'pending')
                const pendingCount = loadedReceivedCases.filter(receivedCase =>
                    receivedCase.stat === 'pending'
                ).length;
                setPendingReceivedCount(pendingCount);
            } else {
                setErrorReceivedCases(response.data?.message || 'Failed to load received cases');
            }
        } catch (err) {
            setErrorReceivedCases('Failed to load received cases');
            debugError('Error loading received cases:', err);
        } finally {
            setLoadingReceivedCases(false);
        }
    };

    const handleAcceptReceivedCase = async (caseSentId: string) => {
        // Find the received case to show in modal
        const receivedCase = receivedCases.find(c => c.csid === caseSentId);
        if (receivedCase) {
            setAcceptModalCase(receivedCase);
            setShowAcceptCaseModal(true);
        } else {
            addAlert('error', 'Case not found');
        }
    };

    const handleAcceptCaseConfirm = async (data: {
        representedParty?: 'partyA' | 'partyB';
        attorneyId?: string;
        caseNote: string;
    }, requestId: string) => {
        if (!acceptModalCase) return;

        try {
            setAcceptingCaseSentId(acceptModalCase.csid);
            const response = await caseApiService.acceptReceivedCase(
                acceptModalCase.csid,
                undefined, // note parameter (deprecated, using caseNote instead)
                data.representedParty,
                data.attorneyId,
                data.caseNote,
                requestId
            );
            if (response.success) {
                setShowAcceptCaseModal(false);
                setAcceptModalCase(null);
                // Store the accepted case ID for the success modal
                setAcceptedCaseId(response.caseId || null);
                setShowAcceptSuccessModal(true);
                // Reload received cases to reflect the change (force reload)
                await loadReceivedCases(true);
                // Reset to page 1 after accepting
                setCurrentPageReceived(1);
            } else {
                // Check if this is the special error code for rescinded cases
                if (response.errorCode === 'CASE-RESCINDED') {
                    setAcceptErrorMessage(response.message || 'This case was rescinded and can no longer be accepted');
                    setShowAcceptErrorModal(true);
                    setShowAcceptCaseModal(false);
                    setAcceptModalCase(null);
                    // Reload received cases to reflect the rescinded status
                    await loadReceivedCases(true);
                } else {
                    addAlert('error', response.message || 'Failed to accept case');
                }
            }
        } catch (error: any) {
            debugError('Error accepting received case:', error);
            // Check if error details contain the special error code
            if (error?.details?.errorCode === 'CASE-RESCINDED') {
                setAcceptErrorMessage(error.details.message || 'This case was rescinded and can no longer be accepted');
                setShowAcceptErrorModal(true);
                setShowAcceptCaseModal(false);
                setAcceptModalCase(null);
                // Reload received cases to reflect the rescinded status
                await loadReceivedCases(true);
            } else if (isCopyInProgressError(error)) {
                addAlert('info', copyInProgressMessage(error));
            } else {
                addAlert('error', error?.details?.message || error?.message || 'Failed to accept case. Please try again.');
            }
        } finally {
            setAcceptingCaseSentId(null);
        }
    };

    const handleRejectReceivedCase = async (caseSentId: string, rejectionNote?: string) => {
        try {
            setRejectingCaseSentId(caseSentId);
            const response = await caseApiService.rejectReceivedCase(caseSentId, rejectionNote);
            if (response.success) {
                addAlert('success', 'Case rejected successfully');
                // Reload received cases to reflect the change (force reload)
                await loadReceivedCases(true);
                // Reset to page 1 after rejecting
                setCurrentPageReceived(1);
            } else {
                addAlert('error', response.message || 'Failed to reject case');
            }
        } catch (error) {
            debugError('Error rejecting received case:', error);
            addAlert('error', 'Failed to reject case. Please try again.');
        } finally {
            setRejectingCaseSentId(null);
        }
    };

    // requestId: one per click (ReceivedCasesTable); a repeat returns the same copy.
    const handleCopyReceivedCase = async (caseId: string, requestId: string) => {
        try {
            const response = await caseApiService.copyCaseForReceiving(caseId, undefined, requestId);
            if (response.success && response.newCaseId) {
                if (proFeatureTrackerEnabled) featureUsageApiService.track('CopyReceivedCase');
                addAlert('success', 'Case copied successfully');
                // Navigate to the new case
                navigate(`/wizard/IA/${response.newCaseId}/case`);
                // Reload cases to show the new copy
                await loadCases();
            } else {
                addAlert('error', response.message || 'Failed to copy case');
            }
        } catch (error) {
            if (isCopyInProgressError(error)) {
                addAlert('info', copyInProgressMessage(error));
            } else if (isCalcVersionDisabledError(error)) {
                addAlert('error', (error as Error).message || 'That tax year is not available yet.');
            } else {
                debugError('Error copying received case:', error);
                addAlert('error', 'Failed to copy case. Please try again.');
            }
            // Rethrown so a retry keeps this click's request id.
            throw error;
        }
    };

    const handleRescindFile = async (caseSentId: string, cancelNote?: string, caseReceivedIds?: string[]) => {
        try {
            setRescindingCaseSentId(caseSentId);
            const response = await caseApiService.rescindFile(caseSentId, cancelNote, caseReceivedIds);
            if (response.success) {
                addAlert('success', response.message || 'Case file rescinded successfully');
                // Reload sent cases to reflect the change (force reload)
                await loadSentCases(true);
            } else {
                // Check if this is the special error code for already accepted cases
                if (response.errorCode === 'CASE-ALREADY-ACCEPTED') {
                    setRescindErrorMessage(response.message || 'This case cannot be rescinded because it has already been accepted');
                    setShowRescindErrorModal(true);
                    // Reload sent cases to reflect the current status
                    await loadSentCases(true);
                } else {
                    addAlert('error', response.message || 'Failed to rescind case file');
                }
            }
        } catch (error: any) {
            debugError('Error rescinding case file:', error);
            // Check if error details contain the special error code
            if (error?.details?.errorCode === 'CASE-ALREADY-ACCEPTED') {
                setRescindErrorMessage(error.details.message || 'This case cannot be rescinded because it has already been accepted');
                setShowRescindErrorModal(true);
                // Reload sent cases to reflect the current status
                await loadSentCases(true);
            } else {
                addAlert('error', error?.details?.message || error?.message || 'Failed to rescind case file. Please try again.');
            }
        } finally {
            setRescindingCaseSentId(null);
        }
    };

    const handleRescindClick = async (caseSentId: string) => {
        // Always show the recipient selection modal
        setRescindModalCaseSentId(caseSentId);
        setShowRescindRecipientsModal(true);
    };



    const handleInputChange = (field: string, value: string | boolean) => {
        const newFilters = {
            ...searchFilters,
            [field]: value,
        };
        setSearchFilters(newFilters);
        // Reset to page 1 when filters change
        setCurrentPage(1);
        setCurrentPageSent(1);

        // Save to localStorage immediately for My Team's Cases
        if (activeTab === 'myTeam') {
            const rememberMyTeamFilters = localStorage.getItem('rememberMyTeamFilters') !== 'false';
            if (rememberMyTeamFilters) {
                localStorage.setItem('savedMyTeamFilters', JSON.stringify(newFilters));
            }
        }
    };

    // Handler for immediate search (no debouncing) - used for dropdowns and checkboxes
    const handleImmediateSearch = (field: string, value: string | boolean | string[]) => {
        const newFilters = {
            ...searchFilters,
            [field]: value,
        };
        setSearchFilters(newFilters);
        // Reset to page 1 when filters change
        setCurrentPage(1);
        setCurrentPageSent(1);
        // Immediately apply the filter without debouncing
        setDebouncedFilters(newFilters);

        // Save to localStorage immediately for My Team's Cases
        if (activeTab === 'myTeam') {
            const rememberMyTeamFilters = localStorage.getItem('rememberMyTeamFilters') !== 'false';
            if (rememberMyTeamFilters) {
                localStorage.setItem('savedMyTeamFilters', JSON.stringify(newFilters));
            }
        }
    };

    const handleResetFilters = useCallback(() => {
        setSearchFilters(defaultFilters);
        setDebouncedFilters(defaultFilters);
        setCurrentPage(1);
        setCurrentPageSent(1);
        if (activeTab === 'myTeam') {
            const rememberMyTeamFilters = localStorage.getItem('rememberMyTeamFilters') !== 'false';
            if (rememberMyTeamFilters) {
                localStorage.setItem('savedMyTeamFilters', JSON.stringify(defaultFilters));
            }
        } else {
            updateURLParams(defaultFilters);
        }
    }, [defaultFilters, activeTab, updateURLParams]);

    // Handler for updating case status. Allowed on a ProDowngrade-locked case too (status-only
    // write; the contents stay read-only), behind the same edit-guard prompt as any other row.
    const handleStatusUpdate = async (caseId: string, newStatus: string) => {
        await runWithEditGuard(caseId, () => performStatusUpdate(caseId, newStatus));
    };

    const performStatusUpdate = async (caseId: string, newStatus: string) => {
        try {
            // Add case to updating set
            setUpdatingStatus(prev => new Set(prev).add(caseId));

            // Loading state is handled by updatingStatus and spinner overlay

            // Update case status via API
            await caseApiService.updateCaseStatus(caseId, newStatus);

            // Update the local state
            setCases(prevCases =>
                prevCases.map(caseItem =>
                    caseItem.cid === caseId
                        ? { ...caseItem, stat: newStatus }
                        : caseItem
                )
            );

            addAlert('success', `Case status updated to ${newStatus}`);
        } catch (error) {
            debugError('Error updating case status:', error);
            // BillingDunning: BaseApiService already raised the app-level BillingReadOnlyGate
            // modal for this 403, so a second error alert would only repeat it.
            if (isTeamBillingReadOnlyError(error)) {
                return;
            }
            const errorMessage = error instanceof Error ? error.message : 'Failed to update case status. Please try again.';
            addAlert('error', errorMessage);
        } finally {
            // Remove case from updating set
            setUpdatingStatus(prev => {
                const newSet = new Set(prev);
                newSet.delete(caseId);
                return newSet;
            });
        }
    };

    const handlePageChange = (newPage: number) => {
        setCurrentPage(newPage);
    };

    // ConcurrentCaseEditGuard: view-only cases (finalized/archived/2024-or-prior) never get an
    // editing marker (see useCaseEditingLock), so this only ever fires for genuinely editable
    // cases someone else (or the same user, in another tab) currently has open.
    //
    // The case list is a point-in-time snapshot -- caseItem.edby can be stale by the time the
    // user clicks (someone could have started, or finished, editing since the list loaded). So
    // before trusting it, re-check live via getEditingStatus and sync the fresh value back into
    // local state, then decide whether to warn based on that fresh value, not the stale one.
    const handleEditClick = async (caseItem: CaseSummary) => {
        if (!concurrentCaseEditGuardEnabled || !caseItem.cid) {
            navigate(`/wizard/${caseItem.sc ?? 'IA'}/${caseItem.cid}/case`);
            return;
        }

        const caseId = caseItem.cid;
        if (checkingEditStatus.has(caseId)) {
            return;
        }

        setCheckingEditStatus(prev => new Set(prev).add(caseId));
        try {
            const freshStatus = await caseApiService.getEditingStatus(caseId);
            const freshEdby = freshStatus?.edby;

            setCases(prev => prev.map(c => c.cid === caseId ? { ...c, edby: freshStatus?.edby, edbyfn: freshStatus?.edbyfn, edbyln: freshStatus?.edbyln, cled: freshStatus?.clientEditing } : c));

            // A client entering expenses is informational only -- editing the case can't lose their
            // data (client expenses live in a separate blob and integrate only via an explicit
            // merge). So no warning here; the homepage badge is the only surfacing. If a client
            // *submits* while the attorney is in the wizard, the MergePendingModal prompts to import.
            if (freshEdby) {
                setEditGuardCase({ ...caseItem, edby: freshStatus?.edby, edbyfn: freshStatus?.edbyfn, edbyln: freshStatus?.edbyln });
                return;
            }
            navigate(`/wizard/${caseItem.sc ?? 'IA'}/${caseId}/case`);
        } finally {
            setCheckingEditStatus(prev => {
                const newSet = new Set(prev);
                newSet.delete(caseId);
                return newSet;
            });
        }
    };

    const handleDownloadClick = (caseId: string) => {
        // Check if tax year is 2024 or prior - if so, show "not available" modal
        const caseItem = cases.find(c => c.cid === caseId);
        if (caseItem && caseItem.txyr !== undefined && caseItem.txyr <= 2024) {
            setShowDownloadNotAvailableModal(true);
            return;
        }

        setDownloadModalCaseId(caseId);
        setDownloadModalCaseData(null);
        setShowDownloadModal(true);
    };

    useEffect(() => {
        if (!showDownloadModal || !downloadModalCaseId) {
            setDownloadModalCaseData(null);
            return;
        }
        let cancelled = false;
        (async () => {
            try {
                const { data } = await caseApiService.getCaseWithoutSaveCheck(downloadModalCaseId);
                if (!cancelled) {
                    setDownloadModalCaseData(data ?? null);
                }
            } catch {
                if (!cancelled) {
                    setDownloadModalCaseData(null);
                }
            }
        })();
        return () => {
            cancelled = true;
        };
    }, [showDownloadModal, downloadModalCaseId]);

    // Separate function for viewing PDF in browser (opens in new tab)
    const handleViewPdf = async (caseId: string, pdfType: string) => {
        // debugLog(`Opening PDF ${pdfType} for case ${caseId} in new tab`);

        // Get the server URL for viewing the PDF and open it in a new tab
        const viewUrl = pdfApiService.getPdfViewUrl(caseId, pdfType);
        window.open(viewUrl, '_blank', 'noopener,noreferrer');

        addAlert('success', 'PDF opened in new tab');
    };

    const handleWordDownload = async (option: string, affidavitDate?: string | null, affidavitIsBlank?: boolean, affidavitSignatureBlockType?: 'notarySignatureBlock' | 'penaltyOfPerjury') => {
        if (!downloadModalCaseId) return;
        const docType = getPdfMapByName(option);
        if (!docType) return;
        try {
            const parameters: Record<string, string | boolean | number | null> = {};
            if (affidavitDate !== undefined) parameters.affidavitDate = affidavitDate ?? null;
            if (affidavitIsBlank !== undefined) parameters.affidavitIsBlank = affidavitIsBlank;
            if (affidavitSignatureBlockType !== undefined) parameters.affidavitSignatureBlockType = affidavitSignatureBlockType;
            const { blob, fileName } = await wordApiService.generateCaseWord(downloadModalCaseId, docType, Object.keys(parameters).length > 0 ? parameters : undefined);
            wordApiService.downloadWord(blob, fileName);
            addAlert('success', 'File downloaded successfully');
        } catch (err) {
            debugError('Word download failed', err);
            addAlert('error', 'Failed to download Word document');
        }
    };

    // Separate function for downloading PDF
    const handleDownloadPdf = async (caseId: string, pdfType: string, parameters?: Record<string, string | boolean | number | string[] | null>) => {
        // debugLog(`Downloading PDF ${pdfType} for case ${caseId}`);

        const { blob, fileName } = await pdfApiService.generateCasePdf(caseId, pdfType, parameters);
        pdfApiService.downloadPdf(blob, fileName);

        addAlert('success', 'PDF generated and downloaded successfully');
    };

    /** Downloads the Alimony Analysis for the scenarios the user ticked. */
    const handleAlimonyScenarioConfirm = async (selectedLineIds: string[]) => {
        const chosen = alimonyScenarioCase;
        setShowAlimonyScenarioModal(false);
        setAlimonyScenarioCase(null);
        if (!chosen) return;

        setCurrentPdfDocument(ALIMONY_ANALYSIS_DOWNLOAD_OPTION);
        setPdfGenerating(true);
        try {
            await handleDownloadPdf(chosen.caseId, getPdfMapByName(ALIMONY_ANALYSIS_DOWNLOAD_OPTION)!, {
                selectedScenarioLineIds: selectedLineIds,
            });
        } catch (error) {
            debugError('Alimony Analysis download failed:', error);
            addAlert('error', error instanceof Error ? error.message : 'Failed to download the alimony analysis');
        } finally {
            setPdfGenerating(false);
            setCurrentPdfDocument('');
        }
    };

    const handleDownloadOption = async (
        option: string,
        affidavitDate?: string | null,
        affidavitIsBlank?: boolean,
        affidavitSignatureBlockType?: 'notarySignatureBlock' | 'penaltyOfPerjury'
    ) => {
        if (!downloadModalCaseId) {
            return;
        }

        if (option === 'Allocation Analysis') {
            const allocationProgressMessages = [
                'Preparing your case data…',
                'Generating allocation narrative…',
                'This may take a little while…',
                'Building PDF…',
            ];
            const clearAllocationPdfProgressInterval = () => {
                if (allocationPdfProgressIntervalRef.current != null) {
                    clearInterval(allocationPdfProgressIntervalRef.current);
                    allocationPdfProgressIntervalRef.current = null;
                }
            };

            setCurrentPdfDocument(option);
            setPdfSpinnerAiAssisted(true);
            setPdfSpinnerMessage(allocationProgressMessages[0]);
            let progressIndex = 0;
            clearAllocationPdfProgressInterval();
            allocationPdfProgressIntervalRef.current = setInterval(() => {
                progressIndex = (progressIndex + 1) % allocationProgressMessages.length;
                setPdfSpinnerMessage(allocationProgressMessages[progressIndex]);
            }, 4500);

            setPdfGenerating(true);
            try {
                const { blob, fileName } = await pdfApiService.generateCalculonAllocationAdvicePdf(downloadModalCaseId, false);
                pdfApiService.downloadPdf(blob, fileName);
                addAlert('success', 'PDF generated and downloaded successfully');
                setShowDownloadModal(false);
                setDownloadModalCaseId(null);
                setDownloadModalCaseData(null);
            } catch (error) {
                debugError('Error with PDF:', error);
                const errorMessage = error instanceof Error ? error.message : 'Failed to process PDF';
                addAlert('error', errorMessage);
            } finally {
                clearAllocationPdfProgressInterval();
                setPdfSpinnerAiAssisted(false);
                setPdfSpinnerMessage('');
                setPdfGenerating(false);
                setCurrentPdfDocument('');
            }
            return;
        }

        // Alimony Analysis is chosen from, not just downloaded: which scenarios go in is the
        // user's decision, so the click opens the picker and the download happens on confirm.
        if (option === ALIMONY_ANALYSIS_DOWNLOAD_OPTION) {
            setAlimonyScenarioCase({ caseId: downloadModalCaseId, caseData: downloadModalCaseData });
            setShowDownloadModal(false);
            setDownloadModalCaseId(null);
            setDownloadModalCaseData(null);
            setShowAlimonyScenarioModal(true);
            return;
        }

        // Map option names to PDF types
        const pdfType = getPdfMapByName(option);
        if (!pdfType) {
            addAlert('warning', `PDF type "${option}" is not yet implemented`);
            return;
        }

        setCurrentPdfDocument(option);
        setPdfGenerating(true);
        try {
            // Build parameters dictionary if affidavit data is provided
            const parameters: Record<string, string | boolean | null> | undefined =
                (affidavitDate !== undefined || affidavitIsBlank !== undefined || affidavitSignatureBlockType !== undefined)
                    ? {
                        ...(affidavitDate !== undefined && { affidavitDate }),
                        ...(affidavitIsBlank !== undefined && { affidavitIsBlank }),
                        ...(affidavitSignatureBlockType !== undefined && { affidavitSignatureBlockType }),
                    }
                    : undefined;

            // DOWNLOAD FILE
            await handleDownloadPdf(downloadModalCaseId, pdfType, parameters);

            // Close modal after action
            setShowDownloadModal(false);
            setDownloadModalCaseId(null);
            setDownloadModalCaseData(null);
        } catch (error) {
            debugError('Error with PDF:', error);
            const errorMessage = error instanceof Error ? error.message : 'Failed to process PDF';
            addAlert('error', errorMessage);
        } finally {
            setPdfGenerating(false);
            setCurrentPdfDocument('');
        }
    };

    // Handler for opening note edit modal
    const handleOpenNoteModal = useCallback((caseId: string, currentNote: string) => {
        setEditingCaseId(caseId);
        setOriginalNoteValue(currentNote || '');
        setShowNoteModal(true);
    }, []);

    // Handler for closing note modal
    const handleCloseNoteModal = useCallback(() => {
        setShowNoteModal(false);
        setEditingCaseId(null);
        setOriginalNoteValue('');
    }, []);

    // Handler for saving note changes
    const handleSaveNote = useCallback(async () => {
        if (!editingCaseId) return;

        // Check if case is finalized or archived (read-only)
        const caseItem = cases.find(c => c.cid === editingCaseId);
        const isReadOnly = caseItem?.stat === 'finalized' || caseItem?.stat === 'archived';

        if (isReadOnly) return;

        const currentValue = noteInputRef.current?.value || '';

        // Only save if the note actually changed
        if (currentValue !== originalNoteValue) {
            await runWithEditGuard(editingCaseId, async () => {
            try {
                // Add case to updating set
                setUpdatingNotes(prev => new Set(prev).add(editingCaseId));

                // Loading state is handled by updatingNotes and spinner overlay

                // Update case note via API
                await caseApiService.updateCaseNote(editingCaseId, currentValue);

                // Update the local state
                setCases(prevCases =>
                    prevCases.map(caseItem =>
                        caseItem.cid === editingCaseId
                            ? { ...caseItem, n: currentValue }
                            : caseItem
                    )
                );

                addAlert('success', 'Case note updated successfully');
                handleCloseNoteModal();
            } catch (error) {
                debugError('Error updating case note:', error);
                let errorMessage = 'Failed to update case note. Please try again.';

                if (error instanceof Error) {
                    errorMessage = error.message;
                    // If it's a validation error, add more context
                    if (error.message.includes('Validation error') || error.message.includes('StringLength')) {
                        errorMessage = `Case note is too long. ${error.message}`;
                    }
                }

                addAlert('error', errorMessage);
            } finally {
                // Remove case from updating set
                setUpdatingNotes(prev => {
                    const newSet = new Set(prev);
                    newSet.delete(editingCaseId);
                    return newSet;
                });
            }
            });
        } else {
            // No changes, just close the modal
            handleCloseNoteModal();
        }
    }, [editingCaseId, originalNoteValue, addAlert, handleCloseNoteModal, cases, runWithEditGuard]);

    // Handler for toggling favorite status
    const handleToggleFavorite = useCallback(async (caseId: string, currentFavoriteStatus: boolean) => {
        try {
            setUpdatingFavorites(prev => new Set(prev).add(caseId));

            const newFavoriteStatus = !currentFavoriteStatus;
            const result = await caseApiService.toggleCaseFavorite(caseId, newFavoriteStatus);

            if (result.success) {
                // Update the local state
                setCases(prevCases =>
                    prevCases.map(caseItem =>
                        caseItem.cid === caseId
                            ? { ...caseItem, fav: newFavoriteStatus }
                            : caseItem
                    )
                );
                addAlert('success', `Case ${newFavoriteStatus ? 'favorited' : 'unfavorited'}`);
            } else {
                addAlert('error', result.message || 'Failed to update favorited status');
            }
        } catch (error) {
            debugError('Error toggling favorite:', error);
            const errorMessage = error instanceof Error ? error.message : 'Failed to update favorited status';
            addAlert('error', errorMessage);
        } finally {
            setUpdatingFavorites(prev => {
                const newSet = new Set(prev);
                newSet.delete(caseId);
                return newSet;
            });
        }
    }, [addAlert]);

    // Memoized edit button styles
    const editButtonStyles = useMemo(() => ({
        background: 'none',
        border: 'none',
        cursor: 'pointer',
        padding: '4px',
        color: theme.colors.primary,
        fontSize: theme.typography.fontSize.sm,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        opacity: 0.8,
        transition: `all ${theme.transitions.normal}`,
        flexShrink: 0,
        '&:hover': {
            opacity: 1,
            backgroundColor: theme.colors.primary + '10',
        },
    }), [theme]);

    const handleKeyPress = (e: React.KeyboardEvent) => {
        if (e.key === 'Enter') {
            // Immediately apply the current search filters
            setDebouncedFilters(searchFilters);
        }
    };

    const formatDate = (dateString: string) => {
        try {
            const date = new Date(dateString);
            const month = String(date.getMonth() + 1).padStart(2, '0');
            const day = String(date.getDate()).padStart(2, '0');
            const year = date.getFullYear();
            const hours = date.getHours();
            const minutes = String(date.getMinutes()).padStart(2, '0');
            const ampm = hours >= 12 ? 'pm' : 'am';
            const displayHours = String(hours % 12 || 12).padStart(2, '0');

            return `${month}.${day}.${year}\n${displayHours}:${minutes}${ampm}`;
        } catch {
            return dateString;
        }
    };

    // Coarse relative-time string for the "last saved X ago" editing indicator. Only needs
    // minute/hour/day granularity -- not a general-purpose formatter.
    const formatRelativeTime = (dateString: string): string => {
        try {
            const then = new Date(dateString).getTime();
            const diffMs = Date.now() - then;
            const diffMinutes = Math.floor(diffMs / 60000);

            if (diffMinutes < 1) return 'just now';
            if (diffMinutes === 1) return '1 minute ago';
            if (diffMinutes < 60) return `${diffMinutes} minutes ago`;

            const diffHours = Math.floor(diffMinutes / 60);
            if (diffHours === 1) return '1 hour ago';
            if (diffHours < 24) return `${diffHours} hours ago`;

            const diffDays = Math.floor(diffHours / 24);
            if (diffDays === 1) return '1 day ago';
            return `${diffDays} days ago`;
        } catch {
            return dateString;
        }
    };

    const handleSort = (key: string) => {
        let newSort: { key: string; direction: 'asc' | 'desc' } | null;
        if (!sortConfig || sortConfig.key !== key) {
            // First click: sort ascending
            newSort = { key, direction: 'asc' };
        } else if (sortConfig.direction === 'asc') {
            // Second click: sort descending
            newSort = { key, direction: 'desc' };
        } else {
            // Third click: remove sorting
            newSort = null;
        }
        setSortConfig(newSort);

        // Save to localStorage immediately for My Team's Cases
        if (activeTab === 'myTeam') {
            const rememberMyTeamFilters = localStorage.getItem('rememberMyTeamFilters') !== 'false';
            if (rememberMyTeamFilters) {
                localStorage.setItem('savedMyTeamSort', JSON.stringify(newSort));
            }
        }
    };

    const getFilteredCases = useCallback(() => {
        return cases.filter((caseItem) => {
            // Filter by last name (Party A or Party B)
            if (debouncedFilters.lastName) {
                const partyALastName = (caseItem.aln || '').toLowerCase();
                const partyBLastName = (caseItem.bln || '').toLowerCase();
                const searchLastName = debouncedFilters.lastName.toLowerCase();

                if (!partyALastName.includes(searchLastName) && !partyBLastName.includes(searchLastName)) {
                    return false;
                }
            }

            // Filter by first name (Party A or Party B)
            if (debouncedFilters.firstName) {
                const partyAFirstName = (caseItem.afn || '').toLowerCase();
                const partyBFirstName = (caseItem.bfn || '').toLowerCase();
                const searchFirstName = debouncedFilters.firstName.toLowerCase();

                if (!partyAFirstName.includes(searchFirstName) && !partyBFirstName.includes(searchFirstName)) {
                    return false;
                }
            }

            // Filter by notes
            if (debouncedFilters.notes) {
                const notes = (caseItem.n || '').toLowerCase();
                if (!notes.includes(debouncedFilters.notes.toLowerCase())) {
                    return false;
                }
            }

            // Filter by case number
            if (debouncedFilters.caseNumber) {
                const caseNumber = (caseItem.cno || '').toLowerCase();
                if (!caseNumber.includes(debouncedFilters.caseNumber.toLowerCase())) {
                    return false;
                }
            }

            // Filter by user
            if (debouncedFilters.userId !== 'all') {
                const selectedUserId = debouncedFilters.userId;
                const caseAttorneyId = caseItem.atid;

                // Normalize both values for comparison (handle null/undefined, trim, lowercase)
                // This ensures GUID strings match regardless of formatting differences
                const normalizedSelectedId = selectedUserId?.toString().trim().toLowerCase() || '';
                const normalizedCaseAttorneyId = caseAttorneyId?.toString().trim().toLowerCase() || '';

                // If case has no attorney (empty/null/undefined), exclude it when filtering by specific attorney
                // Both values must be non-empty and match exactly
                if (!normalizedCaseAttorneyId || normalizedCaseAttorneyId !== normalizedSelectedId) {
                    return false;
                }
            }

            // Filter by updated by
            if (debouncedFilters.updatedBy !== 'all') {
                const selectedUpdatedBy = debouncedFilters.updatedBy;
                const caseUpdatedBy = caseItem.upby;

                if (caseUpdatedBy !== selectedUpdatedBy) {
                    return false;
                }
            }

            // Filter by updated time
            if (debouncedFilters.updated !== 'all') {
                const caseUpdatedDate = new Date(caseItem.upat);
                const now = new Date();
                let cutoffDate: Date;

                switch (debouncedFilters.updated) {
                    case '60_minutes':
                        cutoffDate = new Date(now.getTime() - 60 * 60 * 1000); // 60 minutes ago
                        break;
                    case 'today':
                        cutoffDate = new Date(now);
                        cutoffDate.setHours(0, 0, 0, 0);
                        break;
                    case '3_days':
                        cutoffDate = new Date(now.getTime() - 3 * 24 * 60 * 60 * 1000); // 3 days ago
                        break;
                    case '7_days':
                        cutoffDate = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000); // 7 days ago
                        break;
                    case '14_days':
                        cutoffDate = new Date(now.getTime() - 14 * 24 * 60 * 60 * 1000); // 14 days ago
                        break;
                    case '30_days':
                        cutoffDate = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000); // 30 days ago
                        break;
                    case '90_days':
                        cutoffDate = new Date(now.getTime() - 90 * 24 * 60 * 60 * 1000); // 90 days ago
                        break;
                    case '365_days':
                        cutoffDate = new Date(now.getTime() - 365 * 24 * 60 * 60 * 1000); // 365 days ago
                        break;
                    default:
                        cutoffDate = new Date(0); // Never filter
                }

                if (caseUpdatedDate < cutoffDate) {
                    return false;
                }
            }

            // Filter by archived status
            if (!debouncedFilters.includeArchived) {
                // Exclude archived cases when toggle is off
                if (caseItem.stat === 'archived') {
                    return false;
                }
            }

            // Filter by favorites only
            if (debouncedFilters.favoritesOnly) {
                if (!caseItem.fav) {
                    return false;
                }
            }

            return true;
        });
    }, [cases, debouncedFilters]);

    const getSortedCases = () => {
        const filteredCases = getFilteredCases();
        let sortedCases = filteredCases;

        if (sortConfig) {
            sortedCases = [...filteredCases].sort((a, b) => {
                let aValue: any;
                let bValue: any;

                switch (sortConfig.key) {
                    case 'parties':
                        const aPartyA = a.afn && a.aln ? `${a.afn} ${a.aln}` : '';
                        const aPartyB = a.bfn && a.bln ? `${a.bfn} ${a.bln}` : '';
                        const bPartyA = b.afn && b.aln ? `${b.afn} ${b.aln}` : '';
                        const bPartyB = b.bfn && b.bln ? `${b.bfn} ${b.bln}` : '';

                        // Sort by petitioner first, then other party
                        const aPetitioner = a.pet === 'B' ? aPartyB : aPartyA;
                        const aOther = a.pet === 'B' ? aPartyA : aPartyB;
                        const bPetitioner = b.pet === 'B' ? bPartyB : bPartyA;
                        const bOther = b.pet === 'B' ? bPartyA : bPartyB;

                        aValue = `${aPetitioner} ${aOther}`.trim();
                        bValue = `${bPetitioner} ${bOther}`.trim();
                        break;
                    case 'taxYear':
                        aValue = a.txyr;
                        bValue = b.txyr;
                        break;
                    case 'notes':
                        aValue = a.n || '';
                        bValue = b.n || '';
                        break;
                    case 'updated':
                        aValue = new Date(a.upat);
                        bValue = new Date(b.upat);
                        break;
                    case 'attorney':
                        aValue = getCaseAttorneySortLabel(a);
                        bValue = getCaseAttorneySortLabel(b);
                        break;
                    case 'status':
                        aValue = a.stat || '';
                        bValue = b.stat || '';
                        break;
                    case 'fav':
                        // Sort favorites first (true before false)
                        aValue = a.fav ? 1 : 0;
                        bValue = b.fav ? 1 : 0;
                        break;
                    default:
                        return 0;
                }

                if (aValue < bValue) {
                    return sortConfig.direction === 'asc' ? -1 : 1;
                }
                if (aValue > bValue) {
                    return sortConfig.direction === 'asc' ? 1 : -1;
                }
                return 0;
            });
        }

        // Apply pagination
        const startIndex = (currentPage - 1) * pageSize;
        const endIndex = startIndex + pageSize;
        return sortedCases.slice(startIndex, endIndex);
    };

    const getSortIcon = (key: string) => {
        if (!sortConfig || sortConfig.key !== key) {
            return <FontAwesomeIcon icon={faSort} style={{ fontSize: theme.typography.fontSize.sm, opacity: 0.5 }} />;
        }
        return sortConfig.direction === 'asc'
            ? <FontAwesomeIcon icon={faSortUp} style={{ fontSize: theme.typography.fontSize.sm }} />
            : <FontAwesomeIcon icon={faSortDown} style={{ fontSize: theme.typography.fontSize.sm }} />;
    };

    const handleSortSent = (key: string) => {
        let newSort: { key: string; direction: 'asc' | 'desc' } | null;
        if (!sortConfigSent || sortConfigSent.key !== key) {
            // First click: sort ascending
            newSort = { key, direction: 'asc' };
        } else if (sortConfigSent.direction === 'asc') {
            // Second click: sort descending
            newSort = { key, direction: 'desc' };
        } else {
            // Third click: remove sorting
            newSort = null;
        }
        setSortConfigSent(newSort);

        // Save to localStorage immediately for Sent tab
        if (activeTab === 'sent') {
            const rememberSentSort = localStorage.getItem('rememberSentSort') !== 'false';
            if (rememberSentSort) {
                localStorage.setItem('savedSentSort', JSON.stringify(newSort));
            }
        }
    };

    const getSortIconSent = (key: string) => {
        if (!sortConfigSent || sortConfigSent.key !== key) {
            return <FontAwesomeIcon icon={faSort} style={{ fontSize: theme.typography.fontSize.sm, opacity: 0.5 }} />;
        }
        return sortConfigSent.direction === 'asc'
            ? <FontAwesomeIcon icon={faSortUp} style={{ fontSize: theme.typography.fontSize.sm }} />
            : <FontAwesomeIcon icon={faSortDown} style={{ fontSize: theme.typography.fontSize.sm }} />;
    };

    const handleSortReceived = (key: string) => {
        let newSort: { key: string; direction: 'asc' | 'desc' } | null;
        if (key === 'canAccept') {
            // Special handling for Action column: cycle through 3 states
            if (!sortConfigReceived || sortConfigReceived.key !== key) {
                // First click: sort by accept button displayed (asc = canAccept)
                newSort = { key, direction: 'asc' };
            } else if (sortConfigReceived.direction === 'asc') {
                // Second click: sort by N/A displayed (desc = rescinded)
                newSort = { key, direction: 'desc' };
            } else {
                // Third click: remove sorting
                newSort = null;
            }
        } else {
            // Standard sorting for other columns
            if (!sortConfigReceived || sortConfigReceived.key !== key) {
                // First click: sort ascending
                newSort = { key, direction: 'asc' };
            } else if (sortConfigReceived.direction === 'asc') {
                // Second click: sort descending
                newSort = { key, direction: 'desc' };
            } else {
                // Third click: remove sorting
                newSort = null;
            }
        }
        setSortConfigReceived(newSort);

        // Save to localStorage immediately for Received tab
        if (activeTab === 'received') {
            const rememberReceivedSort = localStorage.getItem('rememberReceivedSort') !== 'false';
            if (rememberReceivedSort) {
                localStorage.setItem('savedReceivedSort', JSON.stringify(newSort));
            }
        }
    };

    const getSortIconReceived = (key: string) => {
        if (!sortConfigReceived || sortConfigReceived.key !== key) {
            return <FontAwesomeIcon icon={faSort} style={{ fontSize: theme.typography.fontSize.sm, opacity: 0.5 }} />;
        }
        return sortConfigReceived.direction === 'asc'
            ? <FontAwesomeIcon icon={faSortUp} style={{ fontSize: theme.typography.fontSize.sm }} />
            : <FontAwesomeIcon icon={faSortDown} style={{ fontSize: theme.typography.fontSize.sm }} />;
    };

    const getSortedReceivedCases = () => {
        let sortedCases = receivedCases;

        if (sortConfigReceived) {
            sortedCases = [...receivedCases].sort((a, b) => {
                let aValue: any;
                let bValue: any;

                switch (sortConfigReceived.key) {
                    case 'canAccept':
                        if (sortConfigReceived.direction === 'asc') {
                            // Sort by whether accept button is displayed (pending/null status = true)
                            // Use 0 for cases with accept button (to sort them first), 1 for others
                            aValue = (a.stat === 'pending' || !a.stat) ? 0 : 1;
                            bValue = (b.stat === 'pending' || !b.stat) ? 0 : 1;
                        } else {
                            // Sort by whether N/A is displayed (rescinded status = true)
                            // Use 1 for rescinded cases (to sort them first in descending), 0 for others
                            aValue = (a.stat === 'rescinded') ? 1 : 0;
                            bValue = (b.stat === 'rescinded') ? 1 : 0;
                        }
                        break;
                    case 'parties':
                        const aPartyA = a.afn && a.aln ? `${a.afn} ${a.aln}` : '';
                        const aPartyB = a.bfn && a.bln ? `${a.bfn} ${a.bln}` : '';
                        const bPartyA = b.afn && b.aln ? `${b.afn} ${b.aln}` : '';
                        const bPartyB = b.bfn && b.bln ? `${b.bfn} ${b.bln}` : '';

                        // Sort by petitioner first, then other party
                        const aPetitioner = a.pet === 'B' ? aPartyB : aPartyA;
                        const aOther = a.pet === 'B' ? aPartyA : aPartyB;
                        const bPetitioner = b.pet === 'B' ? bPartyB : bPartyA;
                        const bOther = b.pet === 'B' ? bPartyA : bPartyB;

                        aValue = `${aPetitioner} ${aOther}`.trim();
                        bValue = `${bPetitioner} ${bOther}`.trim();
                        break;
                    case 'receivedDate':
                        aValue = new Date(a.rcvAt);
                        bValue = new Date(b.rcvAt);
                        break;
                    case 'receivedFrom':
                        aValue = a.rcvFrom?.userName || '';
                        bValue = b.rcvFrom?.userName || '';
                        break;
                    case 'comments':
                        aValue = a.cm || '';
                        bValue = b.cm || '';
                        break;
                    case 'alsoSentTo':
                        aValue = a.sntTo?.map(r => r.userName).join(', ') || '';
                        bValue = b.sntTo?.map(r => r.userName).join(', ') || '';
                        break;
                    case 'status':
                        aValue = a.stat || '';
                        bValue = b.stat || '';
                        break;
                    default:
                        return 0;
                }

                if (aValue < bValue) {
                    return sortConfigReceived.direction === 'asc' ? -1 : 1;
                }
                if (aValue > bValue) {
                    return sortConfigReceived.direction === 'asc' ? 1 : -1;
                }
                return 0;
            });
        }

        // Apply pagination
        const startIndex = (currentPageReceived - 1) * pageSizeReceived;
        const endIndex = startIndex + pageSizeReceived;
        return sortedCases.slice(startIndex, endIndex);
    };

    const getFilteredSentCases = useCallback(() => {
        // Disable filtering for sent cases to avoid performance issues
        return sentCases;
    }, [sentCases]);

    const getSortedSentCases = () => {
        const filteredCases = getFilteredSentCases();
        let sortedCases = filteredCases;

        if (sortConfigSent) {
            sortedCases = [...filteredCases].sort((a, b) => {
                let aValue: any;
                let bValue: any;

                switch (sortConfigSent.key) {
                    case 'parties':
                        const aPartyA = a.afn && a.aln ? `${a.afn} ${a.aln}` : '';
                        const aPartyB = a.bfn && a.bln ? `${a.bfn} ${a.bln}` : '';
                        const bPartyA = b.afn && b.aln ? `${b.afn} ${b.aln}` : '';
                        const bPartyB = b.bfn && b.bln ? `${b.bfn} ${b.bln}` : '';

                        // Sort by petitioner first, then other party
                        const aPetitioner = a.pet === 'B' ? aPartyB : aPartyA;
                        const aOther = a.pet === 'B' ? aPartyA : aPartyB;
                        const bPetitioner = b.pet === 'B' ? bPartyB : bPartyA;
                        const bOther = b.pet === 'B' ? bPartyA : bPartyB;

                        aValue = `${aPetitioner} ${aOther}`.trim();
                        bValue = `${bPetitioner} ${bOther}`.trim();
                        break;
                    case 'sentTo':
                        aValue = a.sentTo.map(r => `${r.userName}${r.teamName ? ` (${r.teamName})` : ''}`).join(', ') || '';
                        bValue = b.sentTo.map(r => `${r.userName}${r.teamName ? ` (${r.teamName})` : ''}`).join(', ') || '';
                        break;
                    case 'sent':
                        aValue = new Date(a.sendDate);
                        bValue = new Date(b.sendDate);
                        break;
                    case 'canRescind':
                        if (sortConfigSent.direction === 'asc') {
                            // First click: Sort by whether rescind button is displayed (true first)
                            // Use 0 for cases with rescind button (to sort them first), 1 for others
                            aValue = a.canRescind ? 0 : 1;
                            bValue = b.canRescind ? 0 : 1;
                        } else {
                            // Second click: Sort by whether rescind button is displayed (true last)
                            // Use 0 for cases with rescind button (to sort them last in descending), 1 for others
                            // In descending, higher values come first, so rescindable (0) comes after non-rescindable (1)
                            aValue = a.canRescind ? 0 : 1;
                            bValue = b.canRescind ? 0 : 1;
                        }
                        break;
                    case 'status':
                        aValue = a.status || '';
                        bValue = b.status || '';
                        break;
                    case 'on':
                        // Sort by status date (accepted date or rescinded date)
                        const aDate = a.status === 'Accepted' ? a.statusDate : (a.status === 'Rescinded' ? a.rescindedDate : null);
                        const bDate = b.status === 'Accepted' ? b.statusDate : (b.status === 'Rescinded' ? b.rescindedDate : null);
                        aValue = aDate ? new Date(aDate) : new Date(0);
                        bValue = bDate ? new Date(bDate) : new Date(0);
                        break;
                    case 'notes':
                        aValue = a.messageNote || '';
                        bValue = b.messageNote || '';
                        break;
                    default:
                        return 0;
                }

                if (aValue < bValue) {
                    return sortConfigSent.direction === 'asc' ? -1 : 1;
                }
                if (aValue > bValue) {
                    return sortConfigSent.direction === 'asc' ? 1 : -1;
                }
                return 0;
            });
        }

        // Apply pagination
        const startIndex = (currentPageSent - 1) * pageSizeSent;
        const endIndex = startIndex + pageSizeSent;
        return sortedCases.slice(startIndex, endIndex);
    };

    const getPaginationInfoSent = () => {
        const filteredCases = getFilteredSentCases();
        const totalFiltered = filteredCases.length;
        const totalPages = Math.ceil(totalFiltered / pageSizeSent);
        const startItem = totalFiltered === 0 ? 0 : (currentPageSent - 1) * pageSizeSent + 1;
        const endItem = Math.min(currentPageSent * pageSizeSent, totalFiltered);

        return {
            totalFiltered,
            totalPages,
            startItem,
            endItem,
            hasNextPage: currentPageSent < totalPages,
            hasPrevPage: currentPageSent > 1
        };
    };

    const handlePageChangeSent = (newPage: number) => {
        setCurrentPageSent(newPage);
    };

    const getPaginationInfoReceived = () => {
        const totalFiltered = receivedCases.length;
        const totalPages = Math.ceil(totalFiltered / pageSizeReceived);
        const startItem = totalFiltered === 0 ? 0 : (currentPageReceived - 1) * pageSizeReceived + 1;
        const endItem = Math.min(currentPageReceived * pageSizeReceived, totalFiltered);

        return {
            totalFiltered,
            totalPages,
            startItem,
            endItem,
            hasNextPage: currentPageReceived < totalPages,
            hasPrevPage: currentPageReceived > 1
        };
    };

    const handlePageChangeReceived = (newPage: number) => {
        setCurrentPageReceived(newPage);
    };

    const getPaginatedReceivedCases = () => {
        // Use getSortedReceivedCases which already handles pagination
        return getSortedReceivedCases();
    };

    const getTotalFilteredCount = () => {
        const filteredCases = getFilteredCases();
        let sortedCases = filteredCases;

        if (sortConfig) {
            sortedCases = [...filteredCases].sort((a, b) => {
                let aValue: any;
                let bValue: any;

                switch (sortConfig.key) {
                    case 'parties':
                        const aPartyA = a.afn && a.aln ? `${a.afn} ${a.aln}` : '';
                        const aPartyB = a.bfn && a.bln ? `${a.bfn} ${a.bln}` : '';
                        const bPartyA = b.afn && b.aln ? `${b.afn} ${b.aln}` : '';
                        const bPartyB = b.bfn && b.bln ? `${b.bfn} ${b.bln}` : '';

                        // Sort by petitioner first, then other party
                        const aPetitioner = a.pet === 'B' ? aPartyB : aPartyA;
                        const aOther = a.pet === 'B' ? aPartyA : aPartyB;
                        const bPetitioner = b.pet === 'B' ? bPartyB : bPartyA;
                        const bOther = b.pet === 'B' ? bPartyA : bPartyB;

                        aValue = `${aPetitioner} ${aOther}`.trim();
                        bValue = `${bPetitioner} ${bOther}`.trim();
                        break;
                    case 'taxYear':
                        aValue = a.txyr;
                        bValue = b.txyr;
                        break;
                    case 'notes':
                        aValue = a.n || '';
                        bValue = b.n || '';
                        break;
                    case 'updated':
                        aValue = new Date(a.upat);
                        bValue = new Date(b.upat);
                        break;
                    case 'attorney':
                        aValue = getCaseAttorneySortLabel(a);
                        bValue = getCaseAttorneySortLabel(b);
                        break;
                    case 'status':
                        aValue = a.stat || '';
                        bValue = b.stat || '';
                        break;
                    case 'fav':
                        // Sort favorites first (true before false)
                        aValue = a.fav ? 1 : 0;
                        bValue = b.fav ? 1 : 0;
                        break;
                    default:
                        return 0;
                }

                if (aValue < bValue) {
                    return sortConfig.direction === 'asc' ? -1 : 1;
                }
                if (aValue > bValue) {
                    return sortConfig.direction === 'asc' ? 1 : -1;
                }
                return 0;
            });
        }

        return sortedCases.length;
    };

    const getPaginationInfo = () => {
        const totalFiltered = getTotalFilteredCount();
        const totalPages = Math.ceil(totalFiltered / pageSize);
        const startItem = (currentPage - 1) * pageSize + 1;
        const endItem = Math.min(currentPage * pageSize, totalFiltered);

        return {
            totalFiltered,
            totalPages,
            startItem,
            endItem,
            hasNextPage: currentPage < totalPages,
            hasPrevPage: currentPage > 1
        };
    };

    // Calculate total filtered count for flash animation
    const totalFilteredCount = useMemo(() => {
        return getTotalFilteredCount();
    }, [cases, debouncedFilters, sortConfig]);

    // Flash animation when case count changes
    useEffect(() => {
        if (activeTab !== 'myTeam') {
            setPreviousCaseCount(null);
            setIsFlashing(false);
            return;
        }

        // Initialize previousCaseCount on first render
        if (previousCaseCount === null) {
            setPreviousCaseCount(totalFilteredCount);
            return;
        }

        // Flash when count changes
        if (previousCaseCount !== totalFilteredCount) {
            setIsFlashing(true);
            setPreviousCaseCount(totalFilteredCount);

            const timer = setTimeout(() => {
                setIsFlashing(false);
            }, 500); // Half a second

            return () => clearTimeout(timer);
        }
    }, [totalFilteredCount, activeTab]);


    // Show loading state while auth is being checked
    if (authLoading) {
        return (
            <div style={{
                minHeight: '100vh',
                backgroundColor: theme.colors.background,
            }}>
                <Navigation />
                <main style={{
                    maxWidth: '1400px',
                    margin: '0 auto',
                    paddingTop: theme.spacing.lg,
                    paddingBottom: theme.spacing.lg,
                    paddingLeft: theme.spacing.lg,
                    paddingRight: theme.spacing.lg,
                }}>
                    {/* Example: Using ItemCard component instead of manual styling */}
                    {/* <ItemCard variant="default">
                        <div style={{
                            padding: theme.spacing.xl,
                            textAlign: 'center',
                            color: theme.colors.text.muted,
                            fontSize: theme.typography.fontSize.lg,
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            gap: theme.spacing.md,
                        }}>
                    </ItemCard> */}

                    <div style={themedStyles.card.default}>
                        <div style={{
                            padding: theme.spacing.xl,
                            textAlign: 'center',
                            color: theme.colors.text.muted,
                            fontSize: theme.typography.fontSize.lg,
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            gap: theme.spacing.md,
                        }}>
                            <div style={{
                                width: '48px',
                                height: '48px',
                                border: `4px solid ${theme.colors.border.default}`,
                                borderTop: `4px solid ${theme.colors.primary}`,
                                borderRadius: '50%',
                                animation: 'spin 1s linear infinite',
                            }}></div>
                            Loading...
                        </div>
                    </div>
                </main>
            </div>
        );
    }

    return (
        <div style={{
            minHeight: '100vh',
            backgroundColor: theme.colors.background,
        }}>
            <Navigation />

            <main style={{
                maxWidth: '1400px',
                margin: '0 auto',
                paddingTop: theme.spacing.lg,
                paddingBottom: theme.spacing.lg,
                paddingLeft: theme.spacing.lg,
                paddingRight: theme.spacing.lg,
            }}>
                <div style={themedStyles.card.default}>
                    {/* Header */}
                    <div style={{
                        marginBottom: theme.spacing.md,
                        paddingBottom: theme.spacing.md,
                        borderBottom: `1px solid ${theme.colors.border.default}`,
                    }}>
                        <h1 style={themedStyles.text.heading}>TEAM CASES</h1>
                    </div>

                    {/* Add New / Import Buttons */}
                    <div style={{ marginBottom: theme.spacing.lg, display: 'flex', gap: theme.spacing.sm, alignItems: 'center', flexWrap: 'wrap' }}>
                        <CustomTooltip text={noTaxYearTooltip} show={noTaxYearTooltip ? undefined : false}>
                        <button
                            style={isBillingReadOnly || newCaseBlocked ? { ...themedStyles.button.primary, opacity: 0.6, cursor: 'not-allowed' } : themedStyles.button.primary}
                            disabled={authLoading || !currentUser?.team?.teamId || isBillingReadOnly || newCaseBlocked}
                            onClick={() => {
                                try {
                                    const { caseId, caseData } = createNewCase(currentUser, true, calcVersions);
                                    const sc = caseData.basic?.stateCode ?? 'IA';
                                    // Navigate with case data in state so context can initialize it
                                    navigate(`/wizard/${sc}/${caseId}/case`, { state: { newCaseData: caseData } });
                                } catch (error) {
                                    const errorMessage = error instanceof Error ? error.message : 'Failed to create case';
                                    addAlert('error', errorMessage);
                                }
                            }}
                        >
                            <FontAwesomeIcon icon={faPlus} style={{ marginRight: theme.spacing.xs }} />
                            Add New
                        </button>
                        </CustomTooltip>
                        {currentUser?.role === 'Root' && (
                            <>
                                <input
                                    ref={importFileInputRef}
                                    type="file"
                                    accept=".json,application/json"
                                    style={{ display: 'none' }}
                                    onChange={async (e) => {
                                        const file = e.target.files?.[0];
                                        e.target.value = '';
                                        if (!file) return;
                                        setImportingCase(true);
                                        try {
                                            const text = await file.text();
                                            const caseData = JSON.parse(text) as CaseData;
                                            const { data } = await caseApiService.importCase(caseData);
                                            if (data?.basic?.caseId) {
                                                navigate(`/wizard/${data.basic.stateCode ?? 'IA'}/${data.basic.caseId}/case`, { state: { newCaseData: data } });
                                                addAlert('success', 'Case imported successfully.');
                                            } else {
                                                addAlert('error', 'Import succeeded but invalid response.');
                                            }
                                        } catch (err) {
                                            const msg = err instanceof Error ? err.message : 'Failed to import case';
                                            addAlert('error', msg);
                                        } finally {
                                            setImportingCase(false);
                                        }
                                    }}
                                />
                                <button
                                    style={themedStyles.button.secondary}
                                    disabled={authLoading || importingCase || !currentUser?.team?.teamId || isBillingReadOnly}
                                    onClick={() => importFileInputRef.current?.click()}
                                >
                                    <FontAwesomeIcon icon={faFileImport} style={{ marginRight: theme.spacing.xs }} />
                                    {importingCase ? 'Importing...' : 'Import'}
                                </button>
                                <button
                                    style={themedStyles.button.secondary}
                                    disabled={authLoading || !currentUser?.team?.teamId}
                                    onClick={() => setShowExportModal(true)}
                                >
                                    <FontAwesomeIcon icon={faFileExport} style={{ marginRight: theme.spacing.xs }} />
                                    Export
                                </button>
                                <button
                                    style={themedStyles.button.secondary}
                                    disabled={authLoading || !currentUser?.team?.teamId}
                                    onClick={() => setShowExportZipModal(true)}
                                >
                                    <FontAwesomeIcon icon={faFileExport} style={{ marginRight: theme.spacing.xs }} />
                                    Export ZIP
                                </button>
                            </>
                        )}
                    </div>

                    {/* Export Case Modal (Root only) */}
                    {currentUser?.role === 'Root' && (
                        <ExportCaseModal
                            isOpen={showExportModal}
                            onClose={() => setShowExportModal(false)}
                        />
                    )}
                    {/* Export Case ZIP Modal (Root only) */}
                    {currentUser?.role === 'Root' && (
                        <ExportCaseZipModal
                            isOpen={showExportZipModal}
                            onClose={() => setShowExportZipModal(false)}
                        />
                    )}

                    {/* Tab Navigation and Item Count */}
                    <div style={{
                        marginTop: theme.spacing.lg,
                    }}>
                        <div className="rw-wrap" style={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            borderBottom: `1px solid ${theme.colors.border.default}`,
                        }}>
                            <ScrollableTabStrip activeKey={activeTab} style={{
                                display: 'inline-flex',
                                backgroundColor: theme.colors.surfaceSecondary,
                                borderRadius: theme.borderRadius.md,
                                padding: theme.spacing.xs,
                                border: `1px solid ${theme.colors.border.default}`,
                            }}>
                                {[
                                    { id: 'myTeam', label: 'My Team\'s Cases' },
                                    { id: 'received', label: 'Received' },
                                    { id: 'sent', label: 'Sent' }
                                ].map((tab) => (
                                    <button
                                        key={tab.id}
                                        data-scroll-active={activeTab === tab.id ? 'true' : undefined}
                                        onClick={(e) => {
                                            // If clicking the refresh icon, don't navigate
                                            if ((e.target as HTMLElement).closest('.refresh-icon')) {
                                                e.stopPropagation();
                                                return;
                                            }
                                            if (tab.id === 'received') {
                                                navigate('/cases/received');
                                            } else if (tab.id === 'myTeam') {
                                                navigate('/cases/list');
                                            } else if (tab.id === 'sent') {
                                                navigate('/cases/sent');
                                            }
                                        }}
                                        style={{
                                            padding: `${theme.spacing.sm} ${theme.spacing.md}`,
                                            backgroundColor: activeTab === tab.id ? theme.colors.primary : 'transparent',
                                            color: activeTab === tab.id ? theme.colors.text.inverse : theme.colors.text.primary,
                                            border: 'none',
                                            borderRadius: theme.borderRadius.sm,
                                            fontSize: theme.typography.fontSize.md,
                                            fontWeight: theme.typography.fontWeight.medium,
                                            cursor: 'pointer',
                                            transition: `all ${theme.transitions.normal}`,
                                            minWidth: '120px',
                                            textAlign: 'center',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            gap: theme.spacing.xs,
                                            boxShadow: activeTab === tab.id ? theme.shadows.sm : 'none',
                                            whiteSpace: 'nowrap' as const,
                                        }}
                                        onMouseEnter={e => {
                                            if (activeTab !== tab.id) {
                                                e.currentTarget.style.backgroundColor = theme.colors.primary;
                                                e.currentTarget.style.color = theme.colors.text.inverse || '#fff';
                                                const svg = e.currentTarget.querySelector('svg') as HTMLElement | null;
                                                if (svg) svg.style.color = theme.colors.text.inverse || '#fff';
                                            }
                                        }}
                                        onMouseLeave={e => {
                                            if (activeTab !== tab.id) {
                                                e.currentTarget.style.backgroundColor = 'transparent';
                                                e.currentTarget.style.color = theme.colors.text.primary;
                                                const svg = e.currentTarget.querySelector('svg') as HTMLElement | null;
                                                if (svg) svg.style.color = theme.colors.primary;
                                            }
                                        }}
                                    >
                                        {(tab.id === 'received' || tab.id === 'sent') && (
                                            <FontAwesomeIcon
                                                icon={faGem}
                                                style={{
                                                    fontSize: theme.typography.fontSize.xs,
                                                    color: activeTab === tab.id ? theme.colors.text.inverse : theme.colors.primary,
                                                }}
                                            />
                                        )}
                                        {tab.label}
                                        {tab.id === 'received' && pendingReceivedCount > 0 && (
                                            <span>({pendingReceivedCount})</span>
                                        )}
                                        {tab.id === 'sent' && pendingSentCount > 0 && (
                                            <span>({pendingSentCount})</span>
                                        )}
                                        {tab.id === 'myTeam' && activeTab === 'myTeam' && (
                                            <CustomTooltip text="Refresh cases list">
                                            <span
                                                className="refresh-icon"
                                                onClick={async (e) => {
                                                    e.stopPropagation();
                                                    await loadCases();
                                                }}
                                                style={{
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    cursor: 'pointer',
                                                }}
                                            >
                                                <FontAwesomeIcon
                                                    icon={faSync}
                                                    spin={loading}
                                                    style={{
                                                        fontSize: theme.typography.fontSize.sm,
                                                    }}
                                                />
                                            </span>
                                            </CustomTooltip>
                                        )}
                                        {tab.id === 'received' && activeTab === 'received' && (
                                            <CustomTooltip text="Refresh received cases list">
                                            <span
                                                className="refresh-icon"
                                                onClick={async (e) => {
                                                    e.stopPropagation();
                                                    await loadReceivedCases(true);
                                                }}
                                                style={{
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    cursor: 'pointer',
                                                }}
                                            >
                                                <FontAwesomeIcon
                                                    icon={faSync}
                                                    spin={loadingReceivedCases}
                                                    style={{
                                                        fontSize: theme.typography.fontSize.sm,
                                                    }}
                                                />
                                            </span>
                                            </CustomTooltip>
                                        )}
                                        {tab.id === 'sent' && activeTab === 'sent' && (
                                            <CustomTooltip text="Refresh sent cases list">
                                            <span
                                                className="refresh-icon"
                                                onClick={async (e) => {
                                                    e.stopPropagation();
                                                    await loadSentCases(true);
                                                }}
                                                style={{
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    cursor: 'pointer',
                                                }}
                                            >
                                                <FontAwesomeIcon
                                                    icon={faSync}
                                                    spin={loadingSentCases}
                                                    style={{
                                                        fontSize: theme.typography.fontSize.sm,
                                                    }}
                                                />
                                            </span>
                                            </CustomTooltip>
                                        )}
                                    </button>
                                ))}
                            </ScrollableTabStrip>
                            {activeTab === 'myTeam' && (
                                <div style={{
                                    textAlign: 'right',
                                    padding: `${theme.spacing.sm} 0`,
                                    position: 'relative' as const,
                                    // `width: 100%` claimed the whole row. This is a flex item, so the
                                    // full width became its base size and squeezed the tab strip beside
                                    // it down to a third of its buttons. The strip painted its tabs
                                    // outside that shrunken box, but this box stayed on top of them --
                                    // so the right-hand two thirds of the last tab took the hover and
                                    // the click instead of the tab. Growing into the space that is left
                                    // keeps the count on the right without taking the strip's room.
                                    flex: '1 1 auto',
                                    minWidth: 0,
                                }}>
                                    <span
                                        style={{
                                            fontSize: theme.typography.fontSize.md,
                                            fontWeight: theme.typography.fontWeight.bold,
                                            color: theme.colors.text.primary,
                                            letterSpacing: '0.025em',
                                            position: 'relative' as const,
                                            zIndex: 2,
                                            display: 'inline-block',
                                            padding: `${theme.spacing.xs} ${theme.spacing.sm}`,
                                            borderRadius: theme.borderRadius.sm,
                                            backgroundColor: isFlashing ? theme.colors.quaternary : 'transparent',
                                            transition: 'background-color 0.5s ease-out',
                                        }}
                                    >
                                        {getPaginationInfo().totalFiltered === 0
                                            ? 'No cases found'
                                            : `Showing ${getPaginationInfo().startItem}-${getPaginationInfo().endItem} of ${getPaginationInfo().totalFiltered} cases`
                                        }
                                    </span>
                                </div>
                            )}
                            {activeTab === 'sent' && isPro && (
                                <div style={{
                                    textAlign: 'right',
                                    padding: `${theme.spacing.sm} 0`,
                                    position: 'relative' as const,
                                    // `width: 100%` claimed the whole row. This is a flex item, so the
                                    // full width became its base size and squeezed the tab strip beside
                                    // it down to a third of its buttons. The strip painted its tabs
                                    // outside that shrunken box, but this box stayed on top of them --
                                    // so the right-hand two thirds of the last tab took the hover and
                                    // the click instead of the tab. Growing into the space that is left
                                    // keeps the count on the right without taking the strip's room.
                                    flex: '1 1 auto',
                                    minWidth: 0,
                                }}>
                                    <span
                                        style={{
                                            padding: `${theme.spacing.xs} ${theme.spacing.sm}`,
                                            borderRadius: theme.borderRadius.sm,
                                            backgroundColor: 'transparent',
                                        }}
                                    >
                                        {getPaginationInfoSent().totalFiltered === 0
                                            ? 'No sent cases found'
                                            : `Showing ${getPaginationInfoSent().startItem}-${getPaginationInfoSent().endItem} of ${getPaginationInfoSent().totalFiltered} sent cases`
                                        }
                                    </span>
                                </div>
                            )}
                            {activeTab === 'received' && isPro && !receivedProRequired && (
                                <div style={{
                                    textAlign: 'right',
                                    padding: `${theme.spacing.sm} 0`,
                                    position: 'relative' as const,
                                    // `width: 100%` claimed the whole row. This is a flex item, so the
                                    // full width became its base size and squeezed the tab strip beside
                                    // it down to a third of its buttons. The strip painted its tabs
                                    // outside that shrunken box, but this box stayed on top of them --
                                    // so the right-hand two thirds of the last tab took the hover and
                                    // the click instead of the tab. Growing into the space that is left
                                    // keeps the count on the right without taking the strip's room.
                                    flex: '1 1 auto',
                                    minWidth: 0,
                                }}>
                                    <span
                                        style={{
                                            padding: `${theme.spacing.xs} ${theme.spacing.sm}`,
                                            borderRadius: theme.borderRadius.sm,
                                            backgroundColor: 'transparent',
                                        }}
                                    >
                                        {receivedCases.length === 0
                                            ? 'No received cases found'
                                            : `Showing ${receivedCases.length} received case${receivedCases.length === 1 ? '' : 's'}`
                                        }
                                    </span>
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Search and Filter Section - Only for My Team's Cases */}
                    {activeTab === 'myTeam' && (
                        <div style={themedStyles.card.secondary}>
                            <div style={{
                                display: 'flex',
                                gap: theme.spacing.lg,
                                marginBottom: theme.spacing.md,
                                flexWrap: 'wrap',
                            }}>
                                <div style={{
                                    display: 'flex',
                                    flexDirection: 'column',
                                    gap: theme.spacing.xs,
                                    minWidth: '200px',
                                }}>
                                    <label style={themedStyles.text.label}>Party Name</label>
                                    <div style={{ display: 'flex', gap: theme.spacing.sm }}>
                                        <input
                                            type="text"
                                            placeholder="Last Name"
                                            value={searchFilters.lastName}
                                            onChange={(e) => handleInputChange('lastName', e.target.value)}
                                            onKeyPress={handleKeyPress}
                                            style={{
                                                ...themedStyles.input.default,
                                                border: searchFilters.lastName ? `2px solid ${theme.colors.status?.success || '#10b981'}` : themedStyles.input.default.border,
                                            }}
                                        />
                                        <input
                                            type="text"
                                            placeholder="First Name"
                                            value={searchFilters.firstName}
                                            onChange={(e) => handleInputChange('firstName', e.target.value)}
                                            onKeyPress={handleKeyPress}
                                            style={{
                                                ...themedStyles.input.default,
                                                border: searchFilters.firstName ? `2px solid ${theme.colors.status?.success || '#10b981'}` : themedStyles.input.default.border,
                                            }}
                                        />
                                    </div>
                                </div>

                                <div style={{
                                    display: 'flex',
                                    flexDirection: 'column',
                                    gap: theme.spacing.xs,
                                    minWidth: '200px',
                                }}>
                                    <label style={themedStyles.text.label}>Notes Contain</label>
                                    <input
                                        type="text"
                                        placeholder="Notes"
                                        value={searchFilters.notes}
                                        onChange={(e) => handleInputChange('notes', e.target.value)}
                                        onKeyPress={handleKeyPress}
                                        style={{
                                            ...themedStyles.input.default,
                                            border: searchFilters.notes ? `2px solid ${theme.colors.status?.success || '#10b981'}` : themedStyles.input.default.border,
                                        }}
                                    />
                                </div>

                                <div style={{
                                    display: 'flex',
                                    flexDirection: 'column',
                                    gap: theme.spacing.xs,
                                    minWidth: '200px',
                                }}>
                                    <label style={themedStyles.text.label}>Case #</label>
                                    <input
                                        type="text"
                                        placeholder="Case Number"
                                        value={searchFilters.caseNumber}
                                        onChange={(e) => handleInputChange('caseNumber', e.target.value)}
                                        onKeyPress={handleKeyPress}
                                        style={{
                                            ...themedStyles.input.default,
                                            border: searchFilters.caseNumber ? `2px solid ${theme.colors.status?.success || '#10b981'}` : themedStyles.input.default.border,
                                        }}
                                    />
                                </div>
                            </div>

                            <div style={{
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'flex-end',
                                flexWrap: 'wrap',
                                gap: theme.spacing.md,
                            }}>
                                <div style={{ display: 'flex', gap: theme.spacing.lg, flexWrap: 'wrap' }}>
                                    <div style={{
                                        display: 'flex',
                                        flexDirection: 'column',
                                        gap: theme.spacing.xs,
                                        minWidth: '150px',
                                    }}>
                                        <label style={themedStyles.text.label}>Attorney</label>
                                        <Select
                                            value={attorneyOptions.find(option => option.value === searchFilters.userId) || (attorneyOptions.length > 0 ? attorneyOptions[0] : { value: 'all', label: 'All' })}
                                            onChange={(option) => handleImmediateSearch('userId', option?.value || 'all')}
                                            options={attorneyOptions}
                                            isSearchable={true}
                                            isClearable={false}
                                            formatOptionLabel={(option, { context }) => {
                                                const opt = option as { value: string; label: string; isInactive?: boolean };
                                                if (!opt.isInactive) {
                                                    return opt.label;
                                                }
                                                const base = opt.label.replace(/ \(Inactive\)$/, '');
                                                const inactiveStyle: React.CSSProperties =
                                                    context === 'value'
                                                        ? {
                                                              fontStyle: 'italic',
                                                              color: theme.colors.text.secondary,
                                                          }
                                                        : {
                                                              fontStyle: 'italic',
                                                              color: 'inherit',
                                                              opacity: 0.82,
                                                          };
                                                return (
                                                    <>
                                                        {base}
                                                        <span style={inactiveStyle}> (Inactive)</span>
                                                    </>
                                                );
                                            }}
                                            styles={{
                                                control: (provided, state) => ({
                                                    ...provided,
                                                    minHeight: '40px',
                                                    border: searchFilters.userId && searchFilters.userId !== 'all'
                                                        ? `2px solid ${theme.colors.status?.success || '#10b981'}`
                                                        : `1px solid ${theme.colors.border.default}`,
                                                    borderRadius: theme.borderRadius.sm,
                                                    backgroundColor: theme.colors.surface,
                                                    boxShadow: state.isFocused ? `0 0 0 1px ${theme.colors.primary}` : 'none',
                                                    '&:hover': {
                                                        borderColor: theme.colors.border.focus,
                                                    },
                                                }),
                                                valueContainer: (provided) => ({
                                                    ...provided,
                                                    padding: '2px 8px',
                                                }),
                                                input: (provided) => ({
                                                    ...provided,
                                                    margin: '0px',
                                                    color: theme.colors.text.primary,
                                                }),
                                                indicatorSeparator: () => ({
                                                    display: 'none',
                                                }),
                                                indicatorsContainer: (provided) => ({
                                                    ...provided,
                                                    padding: '0 8px',
                                                }),
                                                dropdownIndicator: (provided) => ({
                                                    ...provided,
                                                    color: theme.colors.text.secondary,
                                                    '&:hover': {
                                                        color: theme.colors.text.primary,
                                                    },
                                                }),
                                                menu: (provided) => ({
                                                    ...provided,
                                                    backgroundColor: theme.colors.surface,
                                                    border: `1px solid ${theme.colors.border.default}`,
                                                    borderRadius: theme.borderRadius.sm,
                                                    boxShadow: theme.shadows.md,
                                                }),
                                                option: (provided, state) => ({
                                                    ...provided,
                                                    backgroundColor: state.isSelected
                                                        ? theme.colors.primary
                                                        : state.isFocused
                                                            ? theme.colors.primary + '20'
                                                            : 'transparent',
                                                    color: state.isSelected
                                                        ? theme.colors.text.inverse
                                                        : theme.colors.text.primary,
                                                    padding: '8px 12px',
                                                    '&:hover': {
                                                        backgroundColor: state.isSelected
                                                            ? theme.colors.primary
                                                            : theme.colors.primary + '20',
                                                    },
                                                }),
                                                singleValue: (provided) => ({
                                                    ...provided,
                                                    color: theme.colors.text.primary,
                                                }),
                                            }}
                                        />
                                    </div>
                                    <div style={{
                                        display: 'flex',
                                        flexDirection: 'column',
                                        gap: theme.spacing.xs,
                                        minWidth: '150px',
                                    }}>
                                        <label style={themedStyles.text.label}>Updated By</label>
                                        <Select
                                            value={updatedByOptions.find(option => option.value === searchFilters.updatedBy) || updatedByOptions[0]}
                                            onChange={(option) => handleImmediateSearch('updatedBy', option?.value || 'all')}
                                            options={updatedByOptions}
                                            isSearchable={true}
                                            isClearable={false}
                                            styles={{
                                                control: (provided, state) => ({
                                                    ...provided,
                                                    minHeight: '40px',
                                                    border: searchFilters.updatedBy && searchFilters.updatedBy !== 'all'
                                                        ? `2px solid ${theme.colors.status?.success || '#10b981'}`
                                                        : `1px solid ${theme.colors.border.default}`,
                                                    borderRadius: theme.borderRadius.sm,
                                                    backgroundColor: theme.colors.surface,
                                                    boxShadow: state.isFocused ? `0 0 0 1px ${theme.colors.primary}` : 'none',
                                                    '&:hover': {
                                                        borderColor: theme.colors.border.focus,
                                                    },
                                                }),
                                                valueContainer: (provided) => ({
                                                    ...provided,
                                                    padding: '2px 8px',
                                                }),
                                                input: (provided) => ({
                                                    ...provided,
                                                    margin: '0px',
                                                    color: theme.colors.text.primary,
                                                }),
                                                indicatorSeparator: () => ({
                                                    display: 'none',
                                                }),
                                                indicatorsContainer: (provided) => ({
                                                    ...provided,
                                                    padding: '0 8px',
                                                }),
                                                dropdownIndicator: (provided) => ({
                                                    ...provided,
                                                    color: theme.colors.text.secondary,
                                                    '&:hover': {
                                                        color: theme.colors.text.primary,
                                                    },
                                                }),
                                                menu: (provided) => ({
                                                    ...provided,
                                                    backgroundColor: theme.colors.surface,
                                                    border: `1px solid ${theme.colors.border.default}`,
                                                    borderRadius: theme.borderRadius.sm,
                                                    boxShadow: theme.shadows.md,
                                                }),
                                                option: (provided, state) => ({
                                                    ...provided,
                                                    backgroundColor: state.isSelected
                                                        ? theme.colors.primary
                                                        : state.isFocused
                                                            ? theme.colors.primary + '20'
                                                            : 'transparent',
                                                    color: state.isSelected
                                                        ? theme.colors.text.inverse
                                                        : theme.colors.text.primary,
                                                    padding: '8px 12px',
                                                    '&:hover': {
                                                        backgroundColor: state.isSelected
                                                            ? theme.colors.primary
                                                            : theme.colors.primary + '20',
                                                    },
                                                }),
                                                singleValue: (provided) => ({
                                                    ...provided,
                                                    color: theme.colors.text.primary,
                                                }),
                                            }}
                                        />
                                    </div>
                                    <div style={{
                                        display: 'flex',
                                        flexDirection: 'column',
                                        gap: theme.spacing.xs,
                                        minWidth: '150px',
                                    }}>
                                        <label style={themedStyles.text.label}>Last Updated</label>
                                        <Select
                                            value={updatedTimeOptions.find(option => option.value === searchFilters.updated) || updatedTimeOptions[0]}
                                            onChange={(option) => handleImmediateSearch('updated', option?.value || 'all')}
                                            options={updatedTimeOptions}
                                            isSearchable={false}
                                            isClearable={false}
                                            styles={{
                                                control: (provided, state) => ({
                                                    ...provided,
                                                    minHeight: '40px',
                                                    border: searchFilters.updated && searchFilters.updated !== 'all'
                                                        ? `2px solid ${theme.colors.status?.success || '#10b981'}`
                                                        : `1px solid ${theme.colors.border.default}`,
                                                    borderRadius: theme.borderRadius.sm,
                                                    backgroundColor: theme.colors.surface,
                                                    boxShadow: state.isFocused ? `0 0 0 1px ${theme.colors.primary}` : 'none',
                                                    '&:hover': {
                                                        borderColor: theme.colors.border.focus,
                                                    },
                                                }),
                                                valueContainer: (provided) => ({
                                                    ...provided,
                                                    padding: '2px 8px',
                                                }),
                                                input: (provided) => ({
                                                    ...provided,
                                                    margin: '0px',
                                                    color: theme.colors.text.primary,
                                                }),
                                                indicatorSeparator: () => ({
                                                    display: 'none',
                                                }),
                                                indicatorsContainer: (provided) => ({
                                                    ...provided,
                                                    padding: '0 8px',
                                                }),
                                                dropdownIndicator: (provided) => ({
                                                    ...provided,
                                                    color: theme.colors.text.secondary,
                                                    '&:hover': {
                                                        color: theme.colors.text.primary,
                                                    },
                                                }),
                                                menu: (provided) => ({
                                                    ...provided,
                                                    backgroundColor: theme.colors.surface,
                                                    border: `1px solid ${theme.colors.border.default}`,
                                                    borderRadius: theme.borderRadius.sm,
                                                    boxShadow: theme.shadows.md,
                                                }),
                                                option: (provided, state) => ({
                                                    ...provided,
                                                    backgroundColor: state.isSelected
                                                        ? theme.colors.primary
                                                        : state.isFocused
                                                            ? theme.colors.primary + '20'
                                                            : 'transparent',
                                                    color: state.isSelected
                                                        ? theme.colors.text.inverse
                                                        : theme.colors.text.primary,
                                                    padding: '8px 12px',
                                                    '&:hover': {
                                                        backgroundColor: state.isSelected
                                                            ? theme.colors.primary
                                                            : theme.colors.primary + '20',
                                                    },
                                                }),
                                                singleValue: (provided) => ({
                                                    ...provided,
                                                    color: theme.colors.text.primary,
                                                }),
                                            }}
                                        />
                                    </div>
                                    <div style={{
                                        display: 'flex',
                                        flexDirection: 'column',
                                        gap: theme.spacing.xs,
                                        minWidth: '150px',
                                        marginTop: '24px', // Align with the Select input field
                                        padding: '2px',
                                        border: searchFilters.includeArchived ? `2px solid ${theme.colors.status?.success || '#10b981'}` : '2px solid transparent',
                                        borderRadius: theme.borderRadius.sm,
                                        boxSizing: 'border-box' as const,
                                    }}>
                                        <CustomToggle
                                            checked={searchFilters.includeArchived}
                                            onChange={(value) => handleImmediateSearch('includeArchived', value)}
                                            label="Include Archived"
                                        />
                                    </div>
                                    <div style={{
                                        display: 'flex',
                                        flexDirection: 'column',
                                        gap: theme.spacing.xs,
                                        minWidth: '150px',
                                        marginTop: '24px', // Align with the Select input field
                                        padding: '2px',
                                        border: searchFilters.favoritesOnly ? `2px solid ${theme.colors.status?.success || '#10b981'}` : '2px solid transparent',
                                        borderRadius: theme.borderRadius.sm,
                                        boxSizing: 'border-box' as const,
                                    }}>
                                        <CustomToggle
                                            checked={searchFilters.favoritesOnly}
                                            onChange={(value) => handleImmediateSearch('favoritesOnly', value)}
                                            label="Favorites Only"
                                            data-tutorial-target="favorite-only-toggle"
                                        />
                                    </div>
                                </div>
                                {hasNonDefaultFilter && (
                                    <div style={{ display: 'flex', alignItems: 'flex-end' }}>
                                        <button
                                            type="button"
                                            onClick={handleResetFilters}
                                            style={{
                                                display: 'inline-flex',
                                                alignItems: 'center',
                                                gap: theme.spacing.sm,
                                                padding: `${theme.spacing.sm} ${theme.spacing.md}`,
                                                fontSize: theme.typography?.fontSize?.sm || '14px',
                                                fontWeight: theme.typography?.fontWeight?.medium || 500,
                                                color: theme.colors.text?.inverse ?? '#ffffff',
                                                backgroundColor: theme.colors.primary,
                                                border: 'none',
                                                borderRadius: theme.borderRadius?.sm || '6px',
                                                cursor: 'pointer',
                                            }}
                                        >
                                            <FontAwesomeIcon icon={faRotateLeft} />
                                            Reset Filter
                                        </button>
                                    </div>
                                )}
                            </div>
                        </div>
                    )}

                    {concurrentCaseEditGuardEnabled && activeTab === 'myTeam' && myStaleEditingCaseIds.length > 0 && (
                        <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: theme.spacing.sm }}>
                            <CustomTooltip
                                text="Clear the 'editing' marker on all of your own cases below -- won't affect a teammate's marker"
                                position="left"
                                maxWidth="280px"
                                show={showBulkClearEditingTooltip}
                            >
                                <button
                                    type="button"
                                    onClick={() => clearEditingMarkers(myStaleEditingCaseIds)}
                                    disabled={myStaleEditingCaseIds.some(id => clearingEditingMarkerIds.has(id))}
                                    onMouseEnter={() => setShowBulkClearEditingTooltip(true)}
                                    onMouseLeave={() => setShowBulkClearEditingTooltip(false)}
                                    style={{
                                        display: 'inline-flex',
                                        alignItems: 'center',
                                        gap: theme.spacing.sm,
                                        padding: `${theme.spacing.sm} ${theme.spacing.md}`,
                                        fontSize: theme.typography?.fontSize?.sm || '14px',
                                        fontWeight: theme.typography?.fontWeight?.medium || 500,
                                        color: theme.colors.status.warning,
                                        backgroundColor: 'transparent',
                                        border: `1px solid ${theme.colors.status.warning}`,
                                        borderRadius: theme.borderRadius?.sm || '6px',
                                        cursor: myStaleEditingCaseIds.some(id => clearingEditingMarkerIds.has(id)) ? 'default' : 'pointer',
                                        opacity: myStaleEditingCaseIds.some(id => clearingEditingMarkerIds.has(id)) ? 0.6 : 1,
                                    }}
                                >
                                    <FontAwesomeIcon icon={faTimes} />
                                    {`Clear "editing" marker on my ${myStaleEditingCaseIds.length} case${myStaleEditingCaseIds.length === 1 ? '' : 's'}`}
                                </button>
                            </CustomTooltip>
                        </div>
                    )}

                    {/* Data Grid */}
                    <div className="rw-scroll-x" style={styles.tableContainer}>
                        {activeTab === 'myTeam' ? (
                            loading ? (
                                <div style={styles.loadingMessage}>
                                    <img
                                        src="/logo-spinner.gif"
                                        alt="Loading..."
                                        style={styles.loadingSpinner}
                                    />
                                    Loading cases...
                                </div>
                            ) : error ? (
                                <div style={styles.errorMessage}>{error}</div>
                            ) : getSortedCases().length === 0 ? (
                                <div style={{
                                    display: 'flex',
                                    flexDirection: 'column',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    padding: theme.spacing.xl,
                                    textAlign: 'center',
                                    color: theme.colors.text.secondary,
                                }}>
                                    <div style={{
                                        fontSize: theme.typography.fontSize.xl,
                                        marginBottom: theme.spacing.sm,
                                    }}>
                                        <i className="fas fa-search" style={{ marginRight: theme.spacing.sm }}></i>
                                        No cases found
                                    </div>
                                    <div style={{
                                        fontSize: theme.typography.fontSize.md,
                                        color: theme.colors.text.muted,
                                        marginBottom: theme.spacing.lg,
                                    }}>
                                        Try altering your search criteria or ...
                                    </div>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: theme.spacing.md, flexWrap: 'wrap', justifyContent: 'center' }}>
                                        {hasNonDefaultFilter && (
                                            <button
                                                type="button"
                                                onClick={handleResetFilters}
                                                style={{
                                                    display: 'inline-flex',
                                                    alignItems: 'center',
                                                    gap: theme.spacing.sm,
                                                    padding: `${theme.spacing.sm} ${theme.spacing.md}`,
                                                    fontSize: theme.typography?.fontSize?.sm || '14px',
                                                    fontWeight: theme.typography?.fontWeight?.medium || 500,
                                                    color: theme.colors.text?.inverse ?? '#ffffff',
                                                    backgroundColor: theme.colors.primary,
                                                    border: 'none',
                                                    borderRadius: theme.borderRadius?.sm || '6px',
                                                    cursor: 'pointer',
                                                }}
                                            >
                                                <FontAwesomeIcon icon={faRotateLeft} />
                                                Reset Filter
                                            </button>
                                        )}
                                        <CustomTooltip text={noTaxYearTooltip} show={noTaxYearTooltip ? undefined : false}>
                                        <button
                                            style={newCaseBlocked ? { ...themedStyles.button.primary, opacity: 0.6, cursor: 'not-allowed' } : themedStyles.button.primary}
                                            disabled={newCaseBlocked}
                                            onClick={() => {
                                                try {
                                                    const { caseId, caseData } = createNewCase(currentUser, true, calcVersions);
                                                    const sc = caseData.basic?.stateCode ?? 'IA';
                                                    navigate(`/wizard/${sc}/${caseId}/case`, { state: { newCaseData: caseData } });
                                                } catch (error) {
                                                    addAlert('error', error instanceof Error ? error.message : 'Failed to create case');
                                                }
                                            }}
                                        >
                                            <i className="fas fa-plus" style={{ marginRight: theme.spacing.xs }}></i>
                                            Add New
                                        </button>
                                        </CustomTooltip>
                                    </div>
                                </div>
                            ) : (
                                <CardTable style={styles.table}>
                                    <thead>
                                        <tr style={styles.tableHeader}>
                                            <th
                                                style={{ ...styles.headerCell, ...styles.sortableHeader }}
                                                onClick={() => handleSort('fav')}
                                            >
                                                Action {getSortIcon('fav')}
                                            </th>
                                            <th
                                                style={{ ...styles.headerCell, ...styles.sortableHeader }}
                                                onClick={() => handleSort('parties')}
                                            >
                                                Parties {getSortIcon('parties')}
                                            </th>
                                            <th
                                                style={{ ...styles.headerCell, ...styles.sortableHeader }}
                                                onClick={() => handleSort('taxYear')}
                                            >
                                                Tax Year {getSortIcon('taxYear')}
                                            </th>
                                            <th
                                                style={{ ...styles.headerCell, ...styles.sortableHeader }}
                                                onClick={() => handleSort('notes')}
                                            >
                                                Notes {getSortIcon('notes')}
                                            </th>
                                            <th
                                                style={{ ...styles.headerCell, ...styles.sortableHeader }}
                                                onClick={() => handleSort('updated')}
                                            >
                                                Updated {getSortIcon('updated')}
                                            </th>
                                            <th
                                                style={{ ...styles.headerCell, ...styles.sortableHeader }}
                                                onClick={() => handleSort('attorney')}
                                            >
                                                Attorney {getSortIcon('attorney')}
                                            </th>
                                            <th
                                                style={{ ...styles.headerCell, ...styles.sortableHeader }}
                                                onClick={() => handleSort('status')}
                                            >
                                                Status {getSortIcon('status')}
                                            </th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {getSortedCases().map((caseItem, index) => {
                                            const isBeingEdited = concurrentCaseEditGuardEnabled && !!caseItem.edby;
                                            // ProDowngrade: locked cases join the existing view-only
                                            // set (finalized / archived / pre-2025), so the edit
                                            // button and note cell follow. The status dropdown does
                                            // not: a status change is allowed on a locked case.
                                            const isProLocked = isCaseProLocked(caseItem);
                                            const isViewOnly = caseItem.stat === 'finalized'
                                                || caseItem.stat === 'archived'
                                                || (caseItem.txyr !== undefined && caseItem.txyr <= 2024)
                                                || isProLocked;
                                            return (
                                            <tr key={caseItem.cid || index} style={{
                                                ...styles.tableRow,
                                                backgroundColor: isBeingEdited
                                                    ? theme.colors.status.warning + '20'
                                                    : (index % 2 === 0 ? 'white' : '#f8f9fa'),
                                            }}>
                                                <td style={styles.cell}>
                                                    <div style={styles.actionButtons}>
                                                        <CustomTooltip
                                                            text={caseItem.fav ? "Remove from favorites" : "Add to favorites"}
                                                            position="top"
                                                            show={hoveredButton?.caseId === (caseItem.cid || `case-${index}`) && hoveredButton?.button === 'star'}
                                                        >
                                                            <button
                                                                data-star-button
                                                                style={{
                                                                    ...styles.updateButton,
                                                                    backgroundColor: 'transparent',
                                                                    border: 'none',
                                                                    padding: '4px',
                                                                    cursor: 'pointer',
                                                                    opacity: updatingFavorites.has(caseItem.cid || '') ? 0.6 : 1,
                                                                }}
                                                                onMouseEnter={() => setHoveredButton({ caseId: caseItem.cid || `case-${index}`, button: 'star' })}
                                                                onMouseLeave={() => setHoveredButton(null)}
                                                                onClick={() => handleToggleFavorite(caseItem.cid || '', caseItem.fav || false)}
                                                                disabled={updatingFavorites.has(caseItem.cid || '')}
                                                            >
                                                                <FontAwesomeIcon
                                                                    icon={faStar}
                                                                    style={{
                                                                        color: caseItem.fav ? theme.colors.primary : theme.colors.text.muted,
                                                                        fontSize: theme.typography.fontSize.md,
                                                                        opacity: caseItem.fav ? 1 : 0.3,
                                                                        filter: caseItem.fav ? 'none' : 'grayscale(100%)',
                                                                    }}
                                                                />
                                                            </button>
                                                        </CustomTooltip>
                                                        <CustomTooltip
                                                            text={isProLocked ? 'View case (read only) — copy it to make edits' : (isViewOnly ? 'View case details' : 'Edit case')}
                                                            position="top"
                                                            show={hoveredButton?.caseId === (caseItem.cid || `case-${index}`) && hoveredButton?.button === 'edit'}
                                                        >
                                                            <button
                                                                data-tutorial-target="edit-button"
                                                                style={{
                                                                    ...styles.updateButton,
                                                                    backgroundColor: isViewOnly
                                                                        ? theme.colors.status.warning
                                                                        : theme.colors.primary,
                                                                    borderColor: isViewOnly
                                                                        ? theme.colors.status.warning
                                                                        : theme.colors.primary,
                                                                    opacity: checkingEditStatus.has(caseItem.cid || '') ? 0.6 : 1,
                                                                }}
                                                                onMouseEnter={() => setHoveredButton({ caseId: caseItem.cid || `case-${index}`, button: 'edit' })}
                                                                onMouseLeave={() => setHoveredButton(null)}
                                                                onClick={() => handleEditClick(caseItem)}
                                                                disabled={checkingEditStatus.has(caseItem.cid || '')}
                                                            >
                                                                {isViewOnly ? (
                                                                    <FontAwesomeIcon icon={faEye} style={styles.buttonIcon} />
                                                                ) : (
                                                                    <FontAwesomeIcon icon={faEdit} style={styles.buttonIcon} />
                                                                )}
                                                            </button>
                                                        </CustomTooltip>
                                                        <CustomTooltip
                                                            text="Copy case"
                                                            position="top"
                                                            show={hoveredButton?.caseId === (caseItem.cid || `case-${index}`) && hoveredButton?.button === 'copy'}
                                                        >
                                                            <button
                                                                data-tutorial-target="copy-button"
                                                                disabled={isBillingReadOnly}
                                                                style={isBillingReadOnly ? { ...styles.copyButton, opacity: 0.6, cursor: 'not-allowed' } : styles.copyButton}
                                                                onMouseEnter={() => setHoveredButton({ caseId: caseItem.cid || `case-${index}`, button: 'copy' })}
                                                                onMouseLeave={() => setHoveredButton(null)}
                                                                onClick={() => {
                                                                    if (caseItem.cid) {
                                                                        setCopyModalCaseId(caseItem.cid);
                                                                        setShowCopyModal(true);
                                                                    }
                                                                }}
                                                            >
                                                                <FontAwesomeIcon icon={faCopy} style={styles.buttonIcon} />
                                                            </button>
                                                        </CustomTooltip>
                                                        <div style={styles.printDropdownContainer} data-print-dropdown>
                                                            <CustomTooltip
                                                                text="Download documents"
                                                                position="top"
                                                                show={hoveredButton?.caseId === (caseItem.cid || `case-${index}`) && hoveredButton?.button === 'download'}
                                                            >
                                                                <button
                                                                    data-tutorial-target="download-button"
                                                                    style={{
                                                                        ...styles.printButton,
                                                                        backgroundColor: (() => {
                                                                            // Check if tax year is 2024 or prior
                                                                            if (caseItem.txyr !== undefined && caseItem.txyr <= 2024) {
                                                                                return theme.colors.status.warning;
                                                                            }
                                                                            // Otherwise check validation status
                                                                            return (caseItem.dcsg !== true || caseItem.dfadw !== true)
                                                                                ? theme.colors.status.warning
                                                                                : theme.colors.primary;
                                                                        })(),
                                                                        borderColor: (() => {
                                                                            // Check if tax year is 2024 or prior
                                                                            if (caseItem.txyr !== undefined && caseItem.txyr <= 2024) {
                                                                                return theme.colors.status.warning;
                                                                            }
                                                                            // Otherwise check validation status
                                                                            return (caseItem.dcsg !== true || caseItem.dfadw !== true)
                                                                                ? theme.colors.status.warning
                                                                                : theme.colors.primary;
                                                                        })(),
                                                                        opacity: (caseItem.txyr !== undefined && caseItem.txyr <= 2024) ? 0.6 : 1,
                                                                    }}
                                                                    onMouseEnter={() => setHoveredButton({ caseId: caseItem.cid || `case-${index}`, button: 'download' })}
                                                                    onMouseLeave={() => setHoveredButton(null)}
                                                                    onClick={() => handleDownloadClick(caseItem.cid || `case-${index}`)}
                                                                >
                                                                    <FontAwesomeIcon icon={faDownload} style={styles.buttonIcon} />
                                                                </button>
                                                            </CustomTooltip>
                                                        </div>
                                                        {/* Last, not first: the gem appears on some rows only, and leading it
                                                            pushed the star and the action buttons across on exactly those rows,
                                                            so nothing in the column lined up. Trailing it, every row's buttons
                                                            sit in the same place whether the case is locked or not. */}
                                                        {isProLocked && (
                                                            <CustomTooltip
                                                                text="Pro case — read only. Click for details."
                                                                position="top"
                                                                show={hoveredButton?.caseId === (caseItem.cid || `case-${index}`) && hoveredButton?.button === 'gem'}
                                                            >
                                                                <button
                                                                    data-pro-locked-button
                                                                    style={{
                                                                        ...styles.updateButton,
                                                                        backgroundColor: 'transparent',
                                                                        border: 'none',
                                                                        padding: '4px',
                                                                        cursor: 'pointer',
                                                                    }}
                                                                    onMouseEnter={() => setHoveredButton({ caseId: caseItem.cid || `case-${index}`, button: 'gem' })}
                                                                    onMouseLeave={() => setHoveredButton(null)}
                                                                    onClick={() => setProLockedInfoCaseId(caseItem.cid || null)}
                                                                >
                                                                    <FontAwesomeIcon
                                                                        icon={faGem}
                                                                        style={{
                                                                            color: theme.colors.primary,
                                                                            fontSize: theme.typography.fontSize.md,
                                                                        }}
                                                                    />
                                                                </button>
                                                            </CustomTooltip>
                                                        )}
                                                    </div>
                                                    {isBeingEdited && (
                                                        <div style={{
                                                            fontSize: theme.typography.fontSize.xs,
                                                            color: theme.colors.status.warning,
                                                            marginTop: theme.spacing.xs,
                                                            whiteSpace: 'nowrap',
                                                        }}>
                                                            {isEditedByCurrentUser(caseItem.edby)
                                                                ? (caseItem.edbytab && caseItem.edbytab !== getTabId() ? 'You are editing (in another tab)' : 'You are editing')
                                                                : `${editorFirstNameLastInitial(caseItem)} is editing`}
                                                            {caseItem.upat && (
                                                                <>
                                                                    <br />
                                                                    {`(last saved ${formatRelativeTime(caseItem.upat)})`}
                                                                    <CustomTooltip
                                                                        text="Refresh who's editing status for all cases"
                                                                        position="top"
                                                                        show={hoveredButton?.caseId === (caseItem.cid || `case-${index}`) && hoveredButton?.button === 'refreshEditing'}
                                                                    >
                                                                        <button
                                                                            type="button"
                                                                            onClick={() => loadCases()}
                                                                            disabled={loading}
                                                                            onMouseEnter={() => setHoveredButton({ caseId: caseItem.cid || `case-${index}`, button: 'refreshEditing' })}
                                                                            onMouseLeave={() => setHoveredButton(null)}
                                                                            style={{
                                                                                background: 'transparent',
                                                                                border: 'none',
                                                                                padding: 0,
                                                                                marginLeft: theme.spacing.xs,
                                                                                cursor: loading ? 'default' : 'pointer',
                                                                                color: theme.colors.status.warning,
                                                                                verticalAlign: 'middle',
                                                                            }}
                                                                        >
                                                                            <FontAwesomeIcon icon={faSync} spin={loading} style={{ fontSize: '10px' }} />
                                                                        </button>
                                                                    </CustomTooltip>
                                                                </>
                                                            )}
                                                            {isEditedByCurrentUser(caseItem.edby) && (
                                                                <CustomTooltip
                                                                    text="Not actually editing? Clear this marker"
                                                                    position="top"
                                                                    show={hoveredButton?.caseId === (caseItem.cid || `case-${index}`) && hoveredButton?.button === 'clearEditing'}
                                                                >
                                                                    <button
                                                                        type="button"
                                                                        onClick={() => clearEditingMarkers([caseItem.cid || ''])}
                                                                        disabled={clearingEditingMarkerIds.has(caseItem.cid || '')}
                                                                        onMouseEnter={() => setHoveredButton({ caseId: caseItem.cid || `case-${index}`, button: 'clearEditing' })}
                                                                        onMouseLeave={() => setHoveredButton(null)}
                                                                        style={{
                                                                            background: 'transparent',
                                                                            border: 'none',
                                                                            padding: 0,
                                                                            marginLeft: theme.spacing.xs,
                                                                            cursor: clearingEditingMarkerIds.has(caseItem.cid || '') ? 'default' : 'pointer',
                                                                            opacity: clearingEditingMarkerIds.has(caseItem.cid || '') ? 0.6 : 1,
                                                                            color: theme.colors.status.warning,
                                                                            verticalAlign: 'middle',
                                                                        }}
                                                                    >
                                                                        <FontAwesomeIcon icon={faTimes} style={{ fontSize: '10px' }} />
                                                                    </button>
                                                                </CustomTooltip>
                                                            )}
                                                        </div>
                                                    )}
                                                    {concurrentCaseEditGuardEnabled && caseItem.cled && (
                                                        <div style={{
                                                            fontSize: theme.typography.fontSize.xs,
                                                            color: theme.colors.primary,
                                                            marginTop: theme.spacing.xs,
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            gap: theme.spacing.xs,
                                                        }}>
                                                            <FontAwesomeIcon icon={faReceipt} style={{ fontSize: '10px' }} />
                                                            <span>Client is entering expenses</span>
                                                        </div>
                                                    )}
                                                </td>
                                                <td style={styles.cell}>
                                                    {renderParties(caseItem)}
                                                </td>
                                                <td style={styles.cell}>{caseItem.txyr}</td>
                                                <td style={{
                                                    ...styles.cell,
                                                    padding: '5px',
                                                    position: 'relative',
                                                    overflow: 'visible',
                                                }}>
                                                    <div style={{ position: 'relative', width: '100%', height: '100%', display: 'block' }}>
                                                        <CustomTooltip
                                                            // ProDowngrade: /update-note rewrites the case document, so the
                                                            // server rejects it for a locked case. Don't offer the editor.
                                                            text={isProLocked ? 'Copy this case to make edits' : 'Click to edit note'}
                                                            position="top"
                                                            show={hoveredNoteCaseId === (caseItem.cid || `case-${index}`)}
                                                        >
                                                            <div
                                                                data-note-field
                                                                style={{
                                                                    backgroundColor: '#dbeafe',
                                                                    padding: `${theme.spacing.xs} ${theme.spacing.sm}`,
                                                                    borderRadius: theme.borderRadius.sm,
                                                                    width: '100%',
                                                                    height: '100%',
                                                                    minHeight: '100%',
                                                                    maxHeight: '176px', // 8 lines: 14px * 1.5 * 8 + 8px padding
                                                                    overflowY: 'auto',
                                                                    overflowX: 'hidden',
                                                                    boxSizing: 'border-box',
                                                                    cursor: isProLocked ? 'not-allowed' : (updatingNotes.has(caseItem.cid || '') ? 'wait' : 'pointer'),
                                                                    opacity: isProLocked ? 0.6 : (updatingNotes.has(caseItem.cid || '') ? 0.6 : 1),
                                                                    display: 'block',
                                                                }}
                                                                onMouseEnter={() => setHoveredNoteCaseId(caseItem.cid || `case-${index}`)}
                                                                onMouseLeave={() => setHoveredNoteCaseId(null)}
                                                                onClick={() => !isProLocked && !updatingNotes.has(caseItem.cid || '') && handleOpenNoteModal(caseItem.cid || '', caseItem.n || '')}
                                                            >
                                                                {caseItem.n ? (
                                                                    <div
                                                                        style={{
                                                                            fontSize: theme.typography.fontSize.sm,
                                                                            color: '#1e40af',
                                                                            wordWrap: 'break-word',
                                                                            whiteSpace: 'normal',
                                                                            lineHeight: '1.5em',
                                                                        }}
                                                                    >
                                                                        <div
                                                                            style={{
                                                                                whiteSpace: 'pre-wrap',
                                                                                overflowWrap: 'anywhere',
                                                                            }}
                                                                        >
                                                                            {caseItem.n}
                                                                        </div>
                                                                    </div>
                                                                ) : (
                                                                    <div
                                                                        style={{
                                                                            fontSize: theme.typography.fontSize.sm,
                                                                            color: theme.colors.text.muted,
                                                                            wordWrap: 'break-word',
                                                                            whiteSpace: 'normal',
                                                                            lineHeight: '1.5em',
                                                                            fontStyle: 'italic',
                                                                        }}
                                                                    >
                                                                        Click here to enter note
                                                                    </div>
                                                                )}
                                                            </div>
                                                        </CustomTooltip>
                                                    </div>
                                                </td>
                                                <td style={styles.dateCell}>
                                                    <div style={{ display: 'flex', alignItems: 'center', gap: theme.spacing.xs }}>
                                                        <span>{formatDate(caseItem.upat)}</span>
                                                        {caseItem.upby && (
                                                            <CustomTooltip
                                                                content={
                                                                    <>
                                                                        <div style={{ fontWeight: theme.typography.fontWeight.medium, marginBottom: theme.spacing.xs }}>
                                                                            Updated by:
                                                                        </div>
                                                                        <div>
                                                                            {getUserFullName(caseItem.upby)}
                                                                        </div>
                                                                    </>
                                                                }
                                                                position="top"
                                                                show={tooltipCaseId === caseItem.cid}
                                                                minWidth="150px"
                                                                maxWidth="250px"
                                                            >
                                                                <div data-info-icon>
                                                                    <FontAwesomeIcon
                                                                        icon={faInfoCircle}
                                                                        style={{
                                                                            color: theme.colors.text.secondary,
                                                                            fontSize: theme.typography.fontSize.sm,
                                                                            cursor: 'pointer',
                                                                            transition: 'color 0.2s ease',
                                                                        }}
                                                                        onClick={(e) => {
                                                                            e.stopPropagation();
                                                                            const newCaseId = tooltipCaseId === caseItem.cid ? null : caseItem.cid || null;
                                                                            setTooltipCaseId(newCaseId);
                                                                            setClickedTooltipCaseId(newCaseId); // Track if it was opened by click
                                                                        }}
                                                                        onMouseEnter={(e) => {
                                                                            e.currentTarget.style.color = theme.colors.primary;
                                                                            setTooltipCaseId(caseItem.cid || null);
                                                                        }}
                                                                        onMouseLeave={(e) => {
                                                                            e.currentTarget.style.color = theme.colors.text.secondary;
                                                                            // Only hide on mouse leave if it wasn't opened by click
                                                                            if (clickedTooltipCaseId !== caseItem.cid) {
                                                                                setTooltipCaseId(null);
                                                                            }
                                                                        }}
                                                                    />
                                                                </div>
                                                            </CustomTooltip>
                                                        )}
                                                    </div>
                                                </td>
                                                <td style={styles.cell}>
                                                    {(() => {
                                                        const name = resolveCaseAttorneyNameFromTeam(caseItem.atid);
                                                        if (name) {
                                                            return name;
                                                        }
                                                        return (
                                                            <span style={{ fontStyle: 'italic', color: theme.colors.text.muted }}>
                                                                Unspecified
                                                            </span>
                                                        );
                                                    })()}
                                                </td>
                                                <td style={styles.cell}>
                                                    <CaseStatusDropdown
                                                        caseItem={caseItem}
                                                        statusOptions={statusOptions}
                                                        isOpen={openStatusDropdown === caseItem.cid}
                                                        isUpdating={updatingStatus.has(caseItem.cid || '')}
                                                        onToggle={(caseId) => setOpenStatusDropdown(openStatusDropdown === caseId ? null : caseId)}
                                                        onSelect={(caseId, newStatus) => {
                                                            handleStatusUpdate(caseId, newStatus);
                                                            setOpenStatusDropdown(null);
                                                        }}
                                                    />
                                                </td>
                                            </tr>
                                            );
                                        })}
                                    </tbody>
                                </CardTable>
                            )
                        ) : activeTab === 'received' ? (
                            // Received tab content. Not Pro: the waiting count and the upgrade prompt only.
                            <ReceivedCasesPanel
                                isPro={isPro}
                                proRequired={receivedProRequired}
                                pendingCount={pendingReceivedCount}
                                onUpgradeClick={() => setShowProUpgradeFromUpsellModal(true)}
                                receivedCases={getPaginatedReceivedCases()}
                                loading={loadingReceivedCases}
                                error={errorReceivedCases}
                                sortConfig={sortConfigReceived}
                                onSort={handleSortReceived}
                                getSortIcon={getSortIconReceived}
                                acceptingCaseSentId={acceptingCaseSentId}
                                rejectingCaseSentId={rejectingCaseSentId}
                                hoveredButton={hoveredButton}
                                onHoverButton={(caseId, button) => setHoveredButton(caseId ? { caseId, button: button || '' } : null)}
                                onAcceptClick={handleAcceptReceivedCase}
                                onRejectClick={handleRejectReceivedCase}
                                onCopyClick={handleCopyReceivedCase}
                                styles={styles}
                            />
                        ) : (
                            // Sent tab content
                            !isPro ? (
                                <SharingUpsell onUpgradeClick={() => setShowProUpgradeFromUpsellModal(true)} />
                            ) : (
                                <SentCasesTable
                                    sentCases={sentCases}
                                    loading={loadingSentCases}
                                    error={errorSentCases}
                                    sortConfig={sortConfigSent}
                                    onSort={handleSortSent}
                                    rescindingCaseSentId={rescindingCaseSentId}
                                    hoveredButton={hoveredButton}
                                    onHoverButton={(caseId, button) => setHoveredButton(caseId ? { caseId, button: button || '' } : null)}
                                    onRescindClick={handleRescindClick}
                                    getSortedCases={getSortedSentCases}
                                    getSortIcon={getSortIconSent}
                                    styles={styles}
                                />
                            )
                        )}
                    </div>

                    {/* Pagination Controls - Only show for My Team's Cases tab */}
                    {activeTab === 'myTeam' && getPaginationInfo().totalPages > 1 && (
                        <div style={styles.paginationContainer}>
                            <button
                                style={{
                                    ...styles.paginationButton,
                                    ...(getPaginationInfo().hasPrevPage ? styles.paginationButtonEnabled : styles.paginationButtonDisabled)
                                }}
                                onClick={() => handlePageChange(currentPage - 1)}
                                disabled={!getPaginationInfo().hasPrevPage}
                            >
                                Previous
                            </button>

                            <div style={styles.pageNumbers}>
                                {Array.from({ length: getPaginationInfo().totalPages }, (_, i) => i + 1)
                                    .filter(page => {
                                        // Show first page, last page, current page, and pages around current page
                                        return page === 1 ||
                                            page === getPaginationInfo().totalPages ||
                                            Math.abs(page - currentPage) <= 2;
                                    })
                                    .map((page, index, array) => {
                                        const elements = [];

                                        // Add ellipsis before if needed
                                        if (index > 0 && page - array[index - 1] > 1) {
                                            elements.push(
                                                <span key={`ellipsis-${page}`} style={styles.ellipsis}>...</span>
                                            );
                                        }

                                        // Add page number
                                        elements.push(
                                            <button
                                                key={page}
                                                style={{
                                                    ...styles.pageNumber,
                                                    ...(page === currentPage ? styles.pageNumberActive : {})
                                                }}
                                                onClick={() => handlePageChange(page)}
                                            >
                                                {page}
                                            </button>
                                        );

                                        return elements;
                                    })}
                            </div>

                            <button
                                style={{
                                    ...styles.paginationButton,
                                    ...(getPaginationInfo().hasNextPage ? styles.paginationButtonEnabled : styles.paginationButtonDisabled)
                                }}
                                onClick={() => handlePageChange(currentPage + 1)}
                                disabled={!getPaginationInfo().hasNextPage}
                            >
                                Next
                            </button>
                        </div>
                    )}

                    {/* Pagination Controls - Show for Sent tab */}
                    {activeTab === 'sent' && isPro && getPaginationInfoSent().totalPages > 1 && (
                        <div style={styles.paginationContainer}>
                            <button
                                style={{
                                    ...styles.paginationButton,
                                    ...(getPaginationInfoSent().hasPrevPage ? styles.paginationButtonEnabled : styles.paginationButtonDisabled)
                                }}
                                onClick={() => handlePageChangeSent(currentPageSent - 1)}
                                disabled={!getPaginationInfoSent().hasPrevPage}
                            >
                                Previous
                            </button>

                            <div style={styles.pageNumbers}>
                                {Array.from({ length: getPaginationInfoSent().totalPages }, (_, i) => i + 1)
                                    .filter(page => {
                                        // Show first page, last page, current page, and pages around current page
                                        return page === 1 ||
                                            page === getPaginationInfoSent().totalPages ||
                                            Math.abs(page - currentPageSent) <= 2;
                                    })
                                    .map((page, index, array) => {
                                        const elements = [];

                                        // Add ellipsis before if needed
                                        if (index > 0 && page - array[index - 1] > 1) {
                                            elements.push(
                                                <span key={`ellipsis-sent-${page}`} style={styles.ellipsis}>...</span>
                                            );
                                        }

                                        // Add page number
                                        elements.push(
                                            <button
                                                key={`sent-page-${page}`}
                                                style={{
                                                    ...styles.pageNumber,
                                                    ...(page === currentPageSent ? styles.pageNumberActive : {})
                                                }}
                                                onClick={() => handlePageChangeSent(page)}
                                            >
                                                {page}
                                            </button>
                                        );

                                        return elements;
                                    })}
                            </div>

                            <button
                                style={{
                                    ...styles.paginationButton,
                                    ...(getPaginationInfoSent().hasNextPage ? styles.paginationButtonEnabled : styles.paginationButtonDisabled)
                                }}
                                onClick={() => handlePageChangeSent(currentPageSent + 1)}
                                disabled={!getPaginationInfoSent().hasNextPage}
                            >
                                Next
                            </button>
                        </div>
                    )}

                    {/* Pagination Controls - Show for Received tab */}
                    {activeTab === 'received' && isPro && !receivedProRequired && getPaginationInfoReceived().totalPages > 1 && (
                        <div style={styles.paginationContainer}>
                            <button
                                style={{
                                    ...styles.paginationButton,
                                    ...(getPaginationInfoReceived().hasPrevPage ? styles.paginationButtonEnabled : styles.paginationButtonDisabled)
                                }}
                                onClick={() => handlePageChangeReceived(currentPageReceived - 1)}
                                disabled={!getPaginationInfoReceived().hasPrevPage}
                            >
                                Previous
                            </button>

                            <div style={styles.pageNumbers}>
                                {Array.from({ length: getPaginationInfoReceived().totalPages }, (_, i) => i + 1)
                                    .filter(page => {
                                        // Show first page, last page, current page, and pages around current page
                                        return page === 1 ||
                                            page === getPaginationInfoReceived().totalPages ||
                                            Math.abs(page - currentPageReceived) <= 2;
                                    })
                                    .map((page, index, array) => {
                                        const elements = [];

                                        // Add ellipsis before if needed
                                        if (index > 0 && page - array[index - 1] > 1) {
                                            elements.push(
                                                <span key={`ellipsis-received-${page}`} style={styles.ellipsis}>...</span>
                                            );
                                        }

                                        // Add page number
                                        elements.push(
                                            <button
                                                key={`received-page-${page}`}
                                                style={{
                                                    ...styles.pageNumber,
                                                    ...(page === currentPageReceived ? styles.pageNumberActive : {})
                                                }}
                                                onClick={() => handlePageChangeReceived(page)}
                                            >
                                                {page}
                                            </button>
                                        );

                                        return elements;
                                    })}
                            </div>

                            <button
                                style={{
                                    ...styles.paginationButton,
                                    ...(getPaginationInfoReceived().hasNextPage ? styles.paginationButtonEnabled : styles.paginationButtonDisabled)
                                }}
                                onClick={() => handlePageChangeReceived(currentPageReceived + 1)}
                                disabled={!getPaginationInfoReceived().hasNextPage}
                            >
                                Next
                            </button>
                        </div>
                    )}
                </div>
            </main>

            {/* Rescind Recipients Selection Modal */}
            {showRescindRecipientsModal && rescindModalCaseSentId && (() => {
                const sentCase = sentCases.find(c => c.csid === rescindModalCaseSentId);
                if (!sentCase) return null;
                return (
                    <RescindRecipientsModal
                        isOpen={showRescindRecipientsModal}
                        caseSentId={rescindModalCaseSentId}
                        recipients={sentCase.sentTo || []}
                        onClose={() => {
                            setShowRescindRecipientsModal(false);
                            setRescindModalCaseSentId(null);
                        }}
                        onRescind={async () => {
                            await loadSentCases(true);
                        }}
                        onRescindError={(message) => {
                            setRescindErrorMessage(message);
                            setShowRescindErrorModal(true);
                        }}
                    />
                );
            })()}

            {/* Rescind Confirmation Modal */}
            {showRescindModal && (
                <div style={styles.modalOverlay} onClick={() => setShowRescindModal(false)}>
                    <div style={styles.modalContent} onClick={(e) => e.stopPropagation()}>
                        <div style={styles.modalHeader}>
                            <h2 style={styles.modalTitle}>Rescind Case File</h2>
                            <button
                                style={styles.modalCloseButton}
                                onClick={() => {
                                    setShowRescindModal(false);
                                    setRescindModalCaseSentId(null);
                                }}
                            >
                                ×
                            </button>
                        </div>
                        <div style={styles.modalBody}>
                            <p style={styles.modalText}>Rescinding a file will prevent the recipient from accessing it.</p>
                            <p style={styles.modalText}>Are you sure you want to rescind this case file?</p>
                            <div style={{
                                display: 'flex',
                                gap: theme.spacing.md,
                                justifyContent: 'flex-end',
                                marginTop: theme.spacing.lg,
                            }}>
                                <button
                                    style={{
                                        ...themedStyles.button.primary,
                                        backgroundColor: theme.colors.status.error,
                                        color: theme.colors.text.inverse,
                                        padding: `${theme.spacing.sm} ${theme.spacing.md}`,
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: theme.spacing.sm,
                                    }}
                                    onClick={async () => {
                                        if (rescindModalCaseSentId) {
                                            await handleRescindFile(rescindModalCaseSentId, editingSentCaseNote?.caseSentId === rescindModalCaseSentId ? editingSentCaseNote.note : undefined);
                                            setShowRescindModal(false);
                                            setRescindModalCaseSentId(null);
                                        }
                                    }}
                                    disabled={rescindingCaseSentId === rescindModalCaseSentId}
                                >
                                    <FontAwesomeIcon icon={faRotateLeft} />
                                    Rescind
                                </button>
                                <button
                                    style={{
                                        ...themedStyles.button.primary,
                                        backgroundColor: 'transparent',
                                        color: theme.colors.text.primary,
                                        border: `1px solid ${theme.colors.border.default}`,
                                        padding: `${theme.spacing.sm} ${theme.spacing.md}`,
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: theme.spacing.sm,
                                    }}
                                    onClick={() => {
                                        setShowRescindModal(false);
                                        setRescindModalCaseSentId(null);
                                    }}
                                >
                                    <FontAwesomeIcon icon={faTimes} />
                                    Cancel
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}


            {/* ConcurrentCaseEditGuard: warn before opening a case someone else (or the same
                user, in another tab/browser) has open */}
            {editGuardCase && (
                <ConfirmationModal
                    isOpen={!!editGuardCase}
                    onClose={() => setEditGuardCase(null)}
                    onConfirm={() => {
                        if (editGuardCase.cid) {
                            const self = isEditedByCurrentUser(editGuardCase.edby);
                            caseApiService.logConcurrencyEvent(editGuardCase.cid, 'edit_anyway', `editingBy=${editGuardCase.edby ?? ''}${self ? ' (self/other-tab)' : ' (other-user)'}`);
                        }
                        navigate(`/wizard/${editGuardCase.sc ?? 'IA'}/${editGuardCase.cid}/case`);
                    }}
                    title={isEditedByCurrentUser(editGuardCase.edby) ? "You're already editing this case" : "Case is being edited"}
                    message={
                        isEditedByCurrentUser(editGuardCase.edby)
                            ? "You already have this case open for editing, possibly in another tab or browser. If you continue, your changes may conflict with each other and some data could be lost."
                            : `${editorFullName(editGuardCase)} is currently editing this case. If you continue, your changes may conflict with theirs and some data could be lost.`
                    }
                    confirmText="Edit Anyway"
                    cancelText="Cancel"
                    confirmButtonStyle="danger"
                />
            )}

            {guardedAction && (
                <ConfirmationModal
                    isOpen={!!guardedAction}
                    onClose={() => setGuardedAction(null)}
                    onConfirm={() => {
                        const action = guardedAction;
                        setGuardedAction(null);
                        action?.run();
                    }}
                    title="Case is being edited"
                    message={`${guardedAction.editorName} currently has this case open for editing. Saving your change now may conflict with theirs. Save anyway?`}
                    confirmText="Save Anyway"
                    cancelText="Cancel"
                    confirmButtonStyle="danger"
                />
            )}

            {/* Copy Modal */}
            {showCopyModal && copyModalCaseId && (
                <CopyCaseModal
                    key={copyModalCaseId}
                    caseId={copyModalCaseId}
                    caseToCopy={cases.find(c => c.cid === copyModalCaseId)}
                    willStripProData={(() => {
                        // ProDowngrade: copying a Pro-locked case strips its Pro-only data.
                        const caseToCopy = cases.find(c => c.cid === copyModalCaseId);
                        return caseToCopy ? isCaseProLocked(caseToCopy) : false;
                    })()}
                    styles={styles}
                    onClose={() => {
                        setShowCopyModal(false);
                        setCopyModalCaseId(null);
                    }}
                    onCopied={async (newCaseId) => {
                        const origSc = cases.find(c => c.cid === copyModalCaseId)?.sc ?? 'IA';
                        setShowCopyModal(false);
                        setCopyModalCaseId(null);
                        // Navigate to the new case
                        navigate(`/wizard/${origSc}/${newCaseId}/case`);
                        // Reload cases to show the new copy
                        await loadCases();
                    }}
                />
            )}

            {/* Download Modal */}
            <CaseListDownloadModal
                isOpen={showDownloadModal}
                onClose={() => {
                    if (!pdfGenerating) {
                        setShowDownloadModal(false);
                        setDownloadModalCaseId(null);
                        setDownloadModalCaseData(null);
                    }
                }}
                onDownload={handleDownloadOption}
                caseId={downloadModalCaseId || undefined}
                stateCode={downloadModalCaseId ? cases.find(c => c.cid === downloadModalCaseId)?.sc : undefined}
                disabled={pdfGenerating}
                childSupportGuidelinesEnabled={downloadModalCaseId ? cases.find(c => c.cid === downloadModalCaseId)?.dcsg : undefined}
                financialDocsEnabled={downloadModalCaseId ? cases.find(c => c.cid === downloadModalCaseId)?.dfadw : undefined}
                caseData={downloadModalCaseData}
                isPro={isPro}
                onHighIncomeEstimateProRequired={() => {
                    setShowDownloadModal(false);
                    setDownloadModalCaseId(null);
                    setDownloadModalCaseData(null);
                    setShowHighIncomePdfProModal(true);
                }}
                onCalculonAllocationAdviceProRequired={() => {
                    setShowDownloadModal(false);
                    setDownloadModalCaseId(null);
                    setDownloadModalCaseData(null);
                    setShowCalculonAllocationPdfProModal(true);
                }}
                onAlimonyAnalysisProRequired={() => {
                    setShowDownloadModal(false);
                    setDownloadModalCaseId(null);
                    setDownloadModalCaseData(null);
                    setShowAlimonyAnalysisPdfProModal(true);
                }}
                onWordDownload={handleWordDownload}
                onWordDocProRequired={() => {
                    setShowDownloadModal(false);
                    setDownloadModalCaseId(null);
                    setDownloadModalCaseData(null);
                    setShowWordDocProModal(true);
                }}
            />

            {/* PDF Generation Spinner */}
            <SpinnerOverlay
                isVisible={pdfGenerating}
                aiAssisted={pdfSpinnerAiAssisted}
                message={
                    pdfSpinnerAiAssisted && pdfSpinnerMessage
                        ? pdfSpinnerMessage
                        : currentPdfDocument
                          ? `Generating ${currentPdfDocument}...`
                          : 'Generating PDF...'
                }
            />

            {/* Note Edit Modal */}
            {(() => {
                // Check if case is finalized or archived (read-only)
                const caseItem = editingCaseId ? cases.find(c => c.cid === editingCaseId) : null;
                const isReadOnly = caseItem?.stat === 'finalized' || caseItem?.stat === 'archived';

                return (
                    <CaseNoteModal
                        isOpen={showNoteModal}
                        isReadOnly={isReadOnly}
                        noteValue={originalNoteValue}
                        onClose={handleCloseNoteModal}
                        onSave={async (value: string) => {
                            if (!editingCaseId) return;

                            // Only save if the note actually changed
                            if (value !== originalNoteValue) {
                                try {
                                    // Add case to updating set
                                    setUpdatingNotes(prev => new Set(prev).add(editingCaseId));

                                    // Update case note via API
                                    await caseApiService.updateCaseNote(editingCaseId, value);

                                    // Update the local state
                                    setCases(prevCases =>
                                        prevCases.map(caseItem =>
                                            caseItem.cid === editingCaseId
                                                ? { ...caseItem, n: value }
                                                : caseItem
                                        )
                                    );

                                    addAlert('success', 'Case note updated successfully');
                                    handleCloseNoteModal();
                                } catch (error) {
                                    debugError('Error updating case note:', error);
                                    const errorMessage = error instanceof Error ? error.message : 'Failed to update case note. Please try again.';
                                    addAlert('error', errorMessage);
                                } finally {
                                    // Remove case from updating set
                                    setUpdatingNotes(prev => {
                                        const newSet = new Set(prev);
                                        newSet.delete(editingCaseId);
                                        return newSet;
                                    });
                                }
                            } else {
                                // No changes, just close the modal
                                handleCloseNoteModal();
                            }
                        }}
                        isSaving={editingCaseId ? updatingNotes.has(editingCaseId) : false}
                        originalValue={originalNoteValue}
                        useUncontrolledInput={true}
                    />
                );
            })()}

            {/* Spinner overlay for loading operations */}
            <SpinnerOverlay
                isVisible={updatingStatus.size > 0 || updatingNotes.size > 0}
                message={updatingStatus.size > 0 ? 'Updating status...' : 'Updating note...'}
            />

            {/* Download Not Available Modal */}
            <DownloadNotAvailableModal
                isOpen={showDownloadNotAvailableModal}
                onClose={() => setShowDownloadNotAvailableModal(false)}
            />

            {/* Tutorial overlay for first-time users */}
            <TutorialOverlay
                isVisible={showTutorial}
                targetElement={tutorialTargetElement}
                title={tutorialTitle}
                description={tutorialDescription}
                onClose={handleTutorialClose}
                onNext={handleTutorialNext}
                onPrevious={handleTutorialPrevious}
                onSkip={handleTutorialSkip}
                showNext={true}
                showPrevious={true}
                currentStep={tutorialStep}
                totalSteps={5}
                position={tutorialPosition}
            />

            {/* Pro Upgrade Modal when opening from Received/Sent tab upsell (ProSelfUpgrade) */}
            <ProUpgradeModal
                isOpen={showProUpgradeFromUpsellModal}
                onClose={() => setShowProUpgradeFromUpsellModal(false)}
                featureDescription="Send and receive cases with other teams."
                title="Upgrade to Pro"
                featureKey="SendAndReceiveCases"
            />

            {/* Pro Upgrade Modal — High Income Estimate PDF from case list download */}
            <ProUpgradeModal
                isOpen={showHighIncomePdfProModal}
                onClose={() => setShowHighIncomePdfProModal(false)}
                featureDescription=""
                title="High Income Estimate PDF"
                featureKey="HighIncomeEstimatePdf"
            />

            <AlimonyScenarioSelectModal
                isOpen={showAlimonyScenarioModal}
                onClose={() => { setShowAlimonyScenarioModal(false); setAlimonyScenarioCase(null); }}
                caseData={alimonyScenarioCase?.caseData}
                onConfirm={handleAlimonyScenarioConfirm}
            />

            <ProUpgradeModal
                isOpen={showAlimonyAnalysisPdfProModal}
                onClose={() => setShowAlimonyAnalysisPdfProModal(false)}
                featureDescription=""
                title="Alimony Analysis PDF"
                featureKey="AlimonyAnalysis"
            />

            <ProUpgradeModal
                isOpen={showCalculonAllocationPdfProModal}
                onClose={() => setShowCalculonAllocationPdfProModal(false)}
                featureDescription=""
                title="Allocation Analysis PDF"
                featureKey="CalculonAllocationAdvicePdf"
            />

            <ProUpgradeModal
                isOpen={showWordDocProModal}
                onClose={() => setShowWordDocProModal(false)}
                featureDescription=""
                title="Word Document Downloads"
                featureKey="WordDocumentDownload"
            />

            {/* ProDowngrade: why this case is read-only, and what the user can do about it. */}
            <InfoModal
                isOpen={!!proLockedInfoCaseId}
                onClose={() => setProLockedInfoCaseId(null)}
                title="Pro Case — Read Only"
            >
                <p>
                    This case uses Pro features and must be copied to make edits to it. You can
                    still copy this case, download its documents, or change its status.
                </p>
                <ProDataReasonsNotice
                    caseId={proLockedInfoCaseId}
                    intro="It is read-only because it holds this Pro-only data, which the copy will not include:"
                />
                <div style={{ display: 'flex', justifyContent: 'flex-end', gap: theme.spacing.sm, marginTop: theme.spacing.md }}>
                    <button
                        style={styles.copyButton}
                        onClick={() => {
                            const caseIdToCopy = proLockedInfoCaseId;
                            setProLockedInfoCaseId(null);
                            setCopyModalCaseId(caseIdToCopy);
                            setShowCopyModal(true);
                        }}
                    >
                        <FontAwesomeIcon icon={faCopy} style={{ marginRight: theme.spacing.xs }} />
                        Copy This Case
                    </button>
                </div>
            </InfoModal>

            {/* Rescind Error Modal */}
            <InfoModal
                isOpen={showRescindErrorModal}
                onClose={() => setShowRescindErrorModal(false)}
                title="Cannot Rescind Case"
            >
                <p>{rescindErrorMessage}</p>
            </InfoModal>

            {/* Accept Error Modal */}
            <InfoModal
                isOpen={showAcceptErrorModal}
                onClose={() => setShowAcceptErrorModal(false)}
                title="Cannot Accept Case"
            >
                <p>{acceptErrorMessage}</p>
            </InfoModal>

            {/* Accept Case Modal */}
            {showAcceptCaseModal && acceptModalCase && (
                <AcceptCaseModal
                    isOpen={showAcceptCaseModal}
                    receivedCase={acceptModalCase}
                    onClose={() => {
                        setShowAcceptCaseModal(false);
                        setAcceptModalCase(null);
                    }}
                    onAccept={handleAcceptCaseConfirm}
                    accepting={acceptingCaseSentId === acceptModalCase.csid}
                />
            )}

            {/* Accept Case Success Modal */}
            <AcceptCaseSuccessModal
                isOpen={showAcceptSuccessModal}
                caseId={acceptedCaseId}
                onClose={() => {
                    setShowAcceptSuccessModal(false);
                    setAcceptedCaseId(null);
                }}
                onGoToCase={() => {
                    if (acceptedCaseId) {
                        navigate(`/wizard/IA/${acceptedCaseId}/case`);
                        setShowAcceptSuccessModal(false);
                        setAcceptedCaseId(null);
                    }
                }}
            />
            {notificationsEnabled && (
                <AnnouncementModal
                    isOpen={showAnnouncementModal}
                    onClose={() => setShowAnnouncementModal(false)}
                />
            )}
        </div>
    );
};
